<template>
  <div class="min-h-screen bg-white">
    <!-- Filters and Products -->
    <div class="container mx-auto px-4">
      <div class="flex flex-col lg:flex-row gap-6 lg:gap-10">
        <!-- Sidebar with Filters - Visible on Desktop only -->
        <div class="hidden lg:block w-[328px] flex-shrink-0">
          <div class="sticky top-8">
            <div> <!-- Removido border border-[#FAFAFA] -->
              <!-- Category Filter -->
              <div class="flex flex-col gap-6 w-[328px] pb-4">
                <!-- Search bar (Figma) -->
                <div class="w-full">
                  <div class="flex items-center justify-between gap-0 border-2 border-black/70 h-16 pl-2 pr-0 w-full">
                    <input
                      v-model="searchQuerySidebar"
                      @keydown.enter="goToSearch"
                      type="text"
                      :placeholder="$t('categoryPage.searchPlaceholderShort')"
                      class="flex-1 px-2 outline-none font-archivo text-[15px] text-black/70 bg-transparent"
                    />
                    <button
                      @click="goToSearch"
                      type="button"
                      class="w-[58px] h-full bg-empire-yellow flex items-center justify-center"
                      :aria-label="$t('header.search')"
                    >
                      <svg class="w-6 h-6 md:w-8 md:h-8" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="11" cy="11" r="7" stroke="#000" stroke-width="2" />
                        <line x1="16.65" y1="16.65" x2="21" y2="21" stroke="#000" stroke-width="2" stroke-linecap="round" />
                      </svg>
                    </button>
                  </div>
                </div>
                <!-- Keywords below the main search -->
                <div v-if="searchChips.length" class="flex flex-col gap-4 w-full mt-4">
                  <div>
                    <span class="font-archivo font-semibold text-[20px] leading-[22px] text-black/70">{{ $t('categoryPage.keywords') }}</span>
                  </div>
                  <div class="flex flex-col items-start gap-2">
                    <div v-for="kw in searchChips" :key="`kw-${kw}`" class="inline-flex items-start gap-2">
                      <span class="font-archivo italic font-bold text-[16px] leading-[18px] text-black/70">{{ kw }}</span>
                      <button type="button" class="w-[18px] h-[18px] grid place-items-center" @click="removeKeyword(kw)" :aria-label="`remove ${kw}`">
                        <svg viewBox="0 0 24 24" class="w-[14px] h-[14px]" fill="#FF0000" xmlns="http://www.w3.org/2000/svg">
                          <path d="M18 6L6 18M6 6l12 12" stroke="#FF0000" stroke-width="2" stroke-linecap="round" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>

                <!-- Selected filters below the main search, removable from here -->
                <div v-if="selectedFilterChips.length" class="flex flex-col gap-4 w-full mt-4">
                  <div class="flex items-center justify-between w-full">
                    <span class="font-archivo font-semibold text-[20px] leading-[22px] text-black/70">{{ $t('categoryPage.selectedFilters') }}</span>
                    <button v-if="selectedFilterChips.length > 1" type="button" class="font-archivo text-[14px] leading-[18px] text-black/70 underline" @click="clearSelectedFilters">{{ $t('categoryPage.clearFilters') }}</button>
                  </div>
                  <div class="flex flex-col items-start gap-2">
                    <div v-for="f in selectedFilterChips" :key="`sf-${f.id}`" class="inline-flex items-start gap-2">
                      <span class="font-archivo text-[16px] leading-[18px] text-black/70">{{ f.rootName }} : <span class="italic font-bold">{{ f.name }}</span></span>
                      <button type="button" class="w-[18px] h-[18px] grid place-items-center" @click="removeSelectedFilter(f.id)" :aria-label="$t('categoryPage.removeFilter', { name: f.name })">
                        <svg viewBox="0 0 24 24" class="w-[14px] h-[14px]" fill="#FF0000" xmlns="http://www.w3.org/2000/svg">
                          <path d="M18 6L6 18M6 6l12 12" stroke="#FF0000" stroke-width="2" stroke-linecap="round" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>


                <!-- FILIALE - HIDDEN -->
                <div v-if="false" class="flex flex-col gap-6 w-[328px]">
                  <div class="flex items-center justify-between w-full h-[72.66px] bg-black border-b-[5px] border-b-empire-yellow transform rotate-[0.21deg] cursor-pointer" @click="isBranchesExpanded = !isBranchesExpanded">
                    <h3 class="font-archivo-narrow font-semibold text-[24px] leading-[72px] text-empire-yellow px-6">
                      {{ $t('categoryPage.branches') }}
                    </h3>
                    <span class="text-empire-yellow pr-4 text-3xl leading-none select-none">{{ isBranchesExpanded ? '−' : '+' }}</span>
                  </div>
                  <div class="border border-[#FAFAFA] transform rotate-[0.21deg]" v-show="isBranchesExpanded">
                    <div class="flex flex-col gap-3 py-3">
                      <label
                        v-for="branch in branchOptions"
                        :key="branch"
                        class="flex items-center px-6 py-1 gap-3 h-[24.09px]"
                      >
                        <input
                          type="checkbox"
                          :value="branch"
                          v-model="selectedBranches"
                          class="w-6 h-6 accent-[#FBBD1E]"
                        />
                        <span class="font-archivo text-[16px] leading-[18px] text-black/70">{{ branch }}</span>
                      </label>
                      <div v-if="branchOptions.length === 0" class="px-6 py-2 text-sm text-black/50 font-archivo">
                        {{ $t('categoryPage.noBranches') }}
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Filter Title -->
                <div class="flex flex-col w-full" :class="isCategoryExpanded ? 'mb-0' : 'mb-6'">
                  <div class="flex items-center justify-between w-full h-[72.66px] bg-black border-b-[5px] border-b-empire-yellow transform rotate-[0.21deg] cursor-pointer" @click="isCategoryExpanded = !isCategoryExpanded">
                    <h3 class="font-archivo-narrow font-semibold text-[24px] leading-[72px] text-empire-yellow px-6">
                      {{ $t('categoryPage.categories') }}
                    </h3>
                    <span class="text-empire-yellow pr-4 text-3xl leading-none select-none">{{ isCategoryExpanded ? '−' : '+' }}</span>
                  </div>
                </div>

                <!-- Hierarchical Category List -->
                <div class="category-filter-container mb-3" v-show="isCategoryExpanded">
                  <div v-if="loadingCategories" class="flex justify-center items-center py-4">
                    <div class="loader-container">
                      <div class="loader-spinner"></div>
                      <div class="loader-text">{{ $t('common.loading') }}...</div>
                    </div>
                  </div>
                  <CategoryTree
                    :categories="displayedCategoryTree"
                    :selected-category="selectedCategory"
                    @select="selectCategory"
                  />
                </div>
              </div>

                <!-- FILTRE PER - HIDDEN -->
                <div v-if="false" class="flex flex-col gap-3 w-[328px] mb-6">
                  <div class="flex items-center justify-between w-full h-[72.66px] bg-black border-b-[5px] border-b-empire-yellow transform rotate-[0.21deg] cursor-pointer" @click="isFilterPerExpanded = !isFilterPerExpanded">
                    <h3 class="font-archivo-narrow font-semibold text-[24px] leading-[72px] text-empire-yellow px-6">
                      {{ $t('categoryPage.filterBy') }}
                    </h3>
                      <span class="text-empire-yellow pr-4 text-3xl leading-none select-none">{{ isFilterPerExpanded ? '−' : '+' }}</span>

                  </div>
                  <div class="border border-[#FAFAFA] transform rotate-[0.21deg]" v-show="isFilterPerExpanded">
                    <div class="flex flex-col gap-3 py-3">
                      <label
                        v-for="option in filterPerOptions"
                        :key="option"
                        class="flex items-center px-6 py-1 gap-3 h-[24.09px]"
                      >
                        <input
                          type="checkbox"
                          :value="option"
                          v-model="selectedFilterPer"
                          class="w-6 h-6 accent-[#FBBD1E]"
                        />
                        <span class="font-archivo text-[16px] leading-[18px] text-black/70">{{ option }}</span>
                      </label>
                      <div v-if="filterPerOptions.length === 0" class="px-6 py-2 text-sm text-black/50 font-archivo">
                        {{ $t('categoryPage.noAdditionalFilters') }}
                      </div>
                    </div>
                  </div>
                </div>


                <!-- Price Filter (temporarily hidden: showPriceFilter) -->
                <div v-if="showPrices && showPriceFilter" class="flex flex-col gap-6 w-[328px] mb-6">
                  <!-- Filter Title -->
                  <div class="flex flex-col w-full">
                    <div class="flex items-center justify-between w-full h-[72.66px] bg-black border-b-[5px] border-b-empire-yellow transform rotate-[0.21deg] cursor-pointer" @click="isPriceExpanded = !isPriceExpanded">
                      <h3 class="font-archivo-narrow font-semibold text-[24px] leading-[72px] text-empire-yellow px-6">
                        {{ $t('categoryPage.price') }}
                      </h3>
                      <span class="text-empire-yellow pr-4 text-3xl leading-none select-none">{{ isPriceExpanded ? '−' : '+' }}</span>
                    </div>

                  </div>

                  <!-- Filter Content -->
                  <div class="px-3" v-show="isPriceExpanded">
                    <div class="flex justify-between items-center mb-2">
                      <span class="font-archivo text-sm">
                        {{ currencySymbol }}{{ priceRange[0] }}
                      </span>
                      <span class="font-archivo text-sm">
                        {{ currencySymbol }}{{ priceRange[1] }}
                      </span>
                    </div>
                    <div class="relative w-full h-8">
                      <div class="absolute w-full h-2 bg-[#E6E6E6] rounded-full top-3"></div>
                      <div
                        class="absolute h-2 bg-black rounded-full top-3"
                        :style="{ left: (priceRange[0] / maxPrice) * 100 + '%', right: 100 - (priceRange[1] / maxPrice) * 100 + '%' }"
                      ></div>
                      <input
                        type="range"
                        :min="0"
                        :max="maxPrice"
                        v-model.number="priceRange[0]"
                        class="absolute w-full h-2 appearance-none bg-transparent pointer-events-none"
                        @mousedown="handleRangeStart"
                        @mouseup="handleRangeEnd"
                        @touchstart="handleRangeStart"
                        @touchend="handleRangeEnd"
                      >
                      <input
                        type="range"
                        :min="0"
                        :max="maxPrice"
                        v-model.number="priceRange[1]"
                        class="absolute w-full h-2 appearance-none bg-transparent pointer-events-none"
                        @mousedown="handleRangeStart"
                        @mouseup="handleRangeEnd"
                        @touchstart="handleRangeStart"
                        @touchend="handleRangeEnd"
                      >
                    </div>
                  </div>
                </div>

                <!-- Dynamic Filters by Root Characteristic -->
                <DynamicFilter
                  v-for="rootFilter in displayedRootFiltersWithCounts"
                  :key="rootFilter.id"
                  :root-filter="rootFilter"
                  :selected-filters="selectedFilters"
                  :initial-expanded="true"
                  :disabled="isApplyingFilters"
                  @update:selectedFilters="selectedFilters = $event"
                />
                <!-- Extra spacing to avoid sticking to the footer when few filters are visible -->
                <div class="h-6"></div>

            </div>
          </div>
        </div>

        <!-- Product Grid -->
        <div class="flex-1">
          <!-- Mobile Filters and Sorting -->
          <div class="flex justify-between items-center p-3 border-2 border-black w-full h-16 mb-6">
            <div class="flex-1 flex items-center">
              <!-- Mobile Filter Button -->
              <button
                @click="isMobileFiltersExpanded = !isMobileFiltersExpanded"
                class="md:hidden flex items-center justify-center w-[36px] h-[36px] bg-black p-[6px]"
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M4 4L9 12V18L15 21V12L20 4H4Z"
                    stroke="#FFDD00"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </button>

              <!-- Results text - Visible on desktop only -->
              <span class="font-inter text-base hidden md:block">
                {{ totalItems === 0
                  ? $t('categoryPage.noResults')
                  : $t('categoryPage.resultsCount', { count: totalItems })
                }}
              </span>
            </div>

            <div class="flex items-center gap-4">
              <!-- Checkbox: Show out of stock (desktop) -->
              <label class="hidden md:inline-flex items-center self-center gap-2 select-none">
                <input type="checkbox" v-model="onlyInStock" class="align-middle" />
                <span class="font-inter font-semibold text-sm leading-none text-black/70">{{ $t('categoryPage.onlyInStock') }}</span>
              </label>

              <!-- Display: Grid/List (desktop) -->
              <div class="hidden md:flex items-center gap-3">
                <span class="font-inter text-base text-black">{{ $t('categoryPage.display') }}</span>
                <div class="flex items-center gap-2">
                  <button
                    type="button"
                    @click="displayMode = 'grid'"
                    :class="[
                      'w-9 h-9 rounded-[4px] flex items-center justify-center border border-black/40',
                      displayMode === 'grid' ? 'bg-empire-yellow text-black' : 'bg-[#1E1E1E] text-white'
                    ]"
                    aria-label="Grid"
                  >
                    <!-- Grid icon -->
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                      <rect x="4" y="4" width="7" height="7" rx="1"/>
                      <rect x="13" y="4" width="7" height="7" rx="1"/>
                      <rect x="4" y="13" width="7" height="7" rx="1"/>
                      <rect x="13" y="13" width="7" height="7" rx="1"/>
                    </svg>
                  </button>
                  <button
                    type="button"
                    @click="displayMode = 'list'"
                    :class="[
                      'w-9 h-9 rounded-[4px] flex items-center justify-center border border-black/40',
                      displayMode === 'list' ? 'bg-empire-yellow text-black' : 'bg-[#1E1E1E] text-white'
                    ]"
                    aria-label="List"
                  >
                    <!-- List icon -->
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect x="4" y="6" width="16" height="2" fill="currentColor"/>
                      <rect x="4" y="11" width="16" height="2" fill="currentColor"/>
                      <rect x="4" y="16" width="16" height="2" fill="currentColor"/>
                    </svg>
                  </button>
                </div>
              </div>

              <!-- Sorting -->
              <div class="w-[240px]">
                <div class="relative w-full">
                  <select
                    v-model="sortBy"
                    class="w-full h-10 px-4 py-2 bg-white border border-[#D9D9D9] font-inter text-sm appearance-none cursor-pointer"
                    @change="handleSort"
                  >
                    <option
                      v-for="option in sortOptions"
                      :key="option.value"
                      :value="option.value"
                    >
                      {{ $t(option.label) }}
                    </option>
                  </select>
                  <div class="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 pointer-events-none">
                    <svg class="w-4 h-4" viewBox="0 0 24 24" fill="#FFDD00">
                      <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z"/>
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </div>

<!-- Mobile Controls: Out of stock + Display -->
<div class="md:hidden flex items-center justify-between gap-3 mb-4">
  <label class="inline-flex items-center gap-2 select-none">
    <input type="checkbox" v-model="onlyInStock" class="align-middle" />
    <span class="font-inter text-sm leading-none text-black/70">{{ $t('categoryPage.onlyInStock') }}</span>
  </label>
  <div class="flex items-center gap-2">
    <button type="button" @click="displayMode = 'grid'"
      :class="['w-9 h-9 rounded-[4px] flex items-center justify-center border border-black/40', displayMode === 'grid' ? 'bg-empire-yellow text-black' : 'bg-[#1E1E1E] text-white']" aria-label="Grid">
      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
        <rect x="4" y="4" width="7" height="7" rx="1"/>
        <rect x="13" y="4" width="7" height="7" rx="1"/>
        <rect x="4" y="13" width="7" height="7" rx="1"/>
        <rect x="13" y="13" width="7" height="7" rx="1"/>
      </svg>
    </button>
    <button type="button" @click="displayMode = 'list'"
      :class="['w-9 h-9 rounded-[4px] flex items-center justify-center border border-black/40', displayMode === 'list' ? 'bg-empire-yellow text-black' : 'bg-[#1E1E1E] text-white']" aria-label="List">
      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
        <rect x="4" y="6" width="16" height="2"/>
        <rect x="4" y="11" width="16" height="2"/>
        <rect x="4" y="16" width="16" height="2"/>
      </svg>
    </button>
  </div>
</div>

<!-- Results text - Visible on mobile only -->
<div class="md:hidden mb-2">
  <span class="font-inter text-sm">
    {{ totalItems === 0
      ? $t('categoryPage.noResults')
      : $t('categoryPage.resultsCount', { count: totalItems })
    }}
  </span>
</div>




            <!-- Search (mobile) - ALWAYS VISIBLE -->
            <div class="mb-3 md:hidden">
              <div class="flex items-center justify-between gap-0 border-2 border-black/70 h-12 md:h-16 pl-2 pr-0 w-full">
                <input
                  v-model="searchQuerySidebar"
                  @keydown.enter="goToSearch"
                  type="text"
                  :placeholder="$t('categoryPage.searchPlaceholderShort')"
                  class="flex-1 px-2 outline-none font-archivo text-[15px] text-black/70 bg-transparent"
                />
                <button
                  @click="goToSearch"
                  type="button"
                  class="w-[58px] h-full bg-empire-yellow flex items-center justify-center"
                  :aria-label="$t('header.search')"
                >
                  <svg class="w-6 h-6 md:w-8 md:h-8" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="11" cy="11" r="7" stroke="#000" stroke-width="2" />
                    <line x1="16.65" y1="16.65" x2="21" y2="21" stroke="#000" stroke-width="2" stroke-linecap="round" />
                  </svg>
                </button>
              </div>
            </div>

            <!-- Keywords (mobile) - ALWAYS VISIBLE -->
            <div v-if="searchChips.length" class="flex flex-col gap-2 w-full mt-2 mb-4 md:hidden">
              <div>
                <span class="font-archivo font-semibold text-[18px] leading-[20px] text-black/70">{{ $t('categoryPage.keywords') }}</span>
              </div>
              <div class="flex flex-col items-start gap-2">
                <div v-for="kw in searchChips" :key="`kw-mobile-sticky-${kw}`" class="inline-flex items-center gap-2">
                  <span class="font-archivo italic font-bold text-[16px] leading-[18px] text-black/70">{{ kw }}</span>
                  <button type="button" class="w-[18px] h-[18px] grid place-items-center" @click="removeKeyword(kw)" :aria-label="`remove ${kw}`">
                    <svg viewBox="0 0 24 24" class="w-[14px] h-[14px]" fill="#FF0000" xmlns="http://www.w3.org/2000/svg">
                      <path d="M18 6L6 18M6 6l12 12" stroke="#FF0000" stroke-width="2" stroke-linecap="round" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>

            <!-- Selected filters below the main search, removable from here -->
            <div v-if="selectedFilterChips.length" class="flex flex-col gap-2 w-full mt-2 mb-4 md:hidden">
              <div class="flex items-center justify-between w-full">
                <span class="font-archivo font-semibold text-[18px] leading-[20px] text-black/70">{{ $t('categoryPage.selectedFilters') }}</span>
                <button v-if="selectedFilterChips.length > 1" type="button" class="font-archivo text-[14px] leading-[18px] text-black/70 underline" @click="clearSelectedFilters">{{ $t('categoryPage.clearFilters') }}</button>
              </div>
              <div class="flex flex-col items-start gap-2">
                <div v-for="f in selectedFilterChips" :key="`sf-mobile-${f.id}`" class="inline-flex items-start gap-2">
                  <span class="font-archivo text-[16px] leading-[18px] text-black/70">{{ f.rootName }} : <span class="italic font-bold">{{ f.name }}</span></span>
                  <button type="button" class="w-[18px] h-[18px] grid place-items-center" @click="removeSelectedFilter(f.id)" :aria-label="$t('categoryPage.removeFilter', { name: f.name })">
                    <svg viewBox="0 0 24 24" class="w-[14px] h-[14px]" fill="#FF0000" xmlns="http://www.w3.org/2000/svg">
                      <path d="M18 6L6 18M6 6l12 12" stroke="#FF0000" stroke-width="2" stroke-linecap="round" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>

          <!-- Expandable Mobile Filters Section -->
          <div
            v-show="isMobileFiltersExpanded"
            class="w-full mb-6 transition-all duration-300"
          >
            <!-- Search (mobile inline) -->
            <div v-if="false" class="mb-3">
              <div class="flex items-center justify-between gap-0 border-2 border-black/70 h-12 md:h-16 pl-2 pr-0 w-full">
                <input
                  v-model="searchQuerySidebar"
                  @keydown.enter="goToSearch"
                  type="text"
                  :placeholder="$t('categoryPage.searchPlaceholderShort')"
                  class="flex-1 px-2 outline-none font-archivo text-[15px] text-black/70 bg-transparent"
                />
                <button
                  @click="goToSearch"
                  type="button"
                  class="w-[58px] h-full bg-empire-yellow flex items-center justify-center"
                  :aria-label="$t('header.search')"
                >
                  <svg class="w-6 h-6 md:w-8 md:h-8" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="11" cy="11" r="7" stroke="#000" stroke-width="2" />
                    <line x1="16.65" y1="16.65" x2="21" y2="21" stroke="#000" stroke-width="2" stroke-linecap="round" />
                  </svg>
                </button>
              </div>
            </div>
            <!-- Keywords (mobile inline) -->
            <div v-if="false && searchChips.length" class="flex flex-col gap-2 w-full mt-2 mb-4">
              <div>
                <span class="font-archivo font-semibold text-[18px] leading-[20px] text-black/70">{{ $t('categoryPage.keywords') }}</span>
              </div>
              <div class="flex flex-col items-start gap-2">
                <div v-for="kw in searchChips" :key="`kw-mobile-${kw}`" class="inline-flex items-center gap-2">
                  <span class="font-archivo italic font-bold text-[16px] leading-[18px] text-black/70">{{ kw }}</span>
                  <button type="button" class="w-[18px] h-[18px] grid place-items-center" @click="removeKeyword(kw)" :aria-label="`remove ${kw}`">
                    <svg viewBox="0 0 24 24" class="w-[14px] h-[14px]" fill="#FF0000" xmlns="http://www.w3.org/2000/svg">
                      <path d="M18 6L6 18M6 6l12 12" stroke="#FF0000" stroke-width="2" stroke-linecap="round" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>


            <!-- FILIALE (mobile inline) - HIDDEN -->
            <div v-if="false" class="mb-6">
              <div class="flex items-center justify-between w-full h-[50px] md:h-[72.66px] bg-black border-b-[3px] md:border-b-[5px] border-b-empire-yellow cursor-pointer md:cursor-default" @click="isBranchesExpanded = !isBranchesExpanded">
                <h3 class="font-archivo-narrow font-semibold text-[20px] md:text-[24px] leading-[50px] md:leading-[72px] text-empire-yellow px-4 md:px-6">
                  {{ $t('categoryPage.branches') }}
                </h3>
                <div class="md:hidden flex items-center pr-4">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" :class="{ 'transform rotate-180': !isBranchesExpanded }">
                    <path d="M6 9L12 15L18 9" stroke="#FFDD00" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </div>
              </div>
              <div v-show="isBranchesExpanded" class="border border-[#FAFAFA] p-4">
                <div class="flex flex-col gap-3">
                  <label
                    v-for="branch in branchOptions"
                    :key="branch"
                    class="flex items-center gap-3 h-[24.09px]"
                  >
                    <input
                      type="checkbox"
                      :value="branch"
                      v-model="selectedBranches"
                      class="w-6 h-6 accent-[#FBBD1E]"
                    >
                    <span class="font-archivo text-[14px] leading-[16px] text-black/70">
                      {{ branch }}
                    </span>
                  </label>
                  <div v-if="branchOptions.length === 0" class="px-4 py-2 text-sm text-black/50 font-archivo">
                    {{ $t('categoryPage.noBranches') }}
                  </div>
                </div>
              </div>
            </div>

            <!-- Category Filter -->
            <div class="mb-6">
              <div @click="isCategoryExpanded = !isCategoryExpanded" class="flex items-center justify-between w-full h-[50px] md:h-[72.66px] bg-black border-b-[3px] md:border-b-[5px] border-b-empire-yellow cursor-pointer md:cursor-default">
                <h3 class="font-archivo-narrow font-semibold text-[20px] md:text-[24px] leading-[50px] md:leading-[72px] text-empire-yellow px-4 md:px-6">
                  {{ $t('categoryPage.categories') }}
                </h3>
                <!-- Yellow arrow to expand/collapse (mobile only) -->
                <div class="md:hidden flex items-center pr-4">

                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" :class="{ 'transform rotate-180': !isCategoryExpanded }">
                    <path d="M6 9L12 15L18 9" stroke="#FFDD00" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </div>
              </div>
              <div v-show="isCategoryExpanded" class="category-filter-container-mobile relative">
                <div v-if="loadingCategories" class="flex justify-center items-center py-2">
                  <div class="loader-container">
                    <div class="loader-spinner"></div>
                    <div class="loader-text">{{ $t('common.loading') }}...</div>
                  </div>
                </div>
                <CategoryTree
                  :categories="displayedCategoryTree"
                  :selected-category="selectedCategory"
                  @select="selectCategory"
                />
              </div>
            </div>
            <!-- FILTRER PAR (mobile inline) - HIDDEN -->
            <div v-if="false" class="mb-6">
              <div class="flex items-center justify-between w-full h-[50px] md:h-[72.66px] bg-black border-b-[3px] md:border-b-[5px] border-b-empire-yellow cursor-pointer md:cursor-default" @click="isFilterPerExpanded = !isFilterPerExpanded">
                <h3 class="font-archivo-narrow font-semibold text-[20px] md:text-[24px] leading-[50px] md:leading-[72px] text-empire-yellow px-4 md:px-6">
                  {{ $t('categoryPage.filterBy') }}
                </h3>
                <div class="md:hidden flex items-center pr-4">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" :class="{ 'transform rotate-180': !isFilterPerExpanded }">
                    <path d="M6 9L12 15L18 9" stroke="#FFDD00" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </div>

              </div>
              <div class="border border-[#FAFAFA] p-4" v-show="isFilterPerExpanded">
                <div class="flex flex-col gap-3">
                  <label
                    v-for="option in filterPerOptions"
                    :key="option"
                    class="flex items-center gap-3 h-[24.09px]"
                  >
                    <input
                      type="checkbox"
                      :value="option"
                      v-model="selectedFilterPer"
                      class="w-6 h-6 accent-[#FBBD1E]"
                    >
                    <span class="font-archivo text-[14px] leading-[16px] text-black/70">
                      {{ option }}
                    </span>
                  </label>
                </div>
              </div>
            </div>


            <!-- Price Filter (temporarily hidden: showPriceFilter) -->
            <div v-if="showPrices && showPriceFilter" class="mb-6">
              <div @click="isPriceExpanded = !isPriceExpanded" class="flex items-center justify-between w-full h-[50px] md:h-[72.66px] bg-black border-b-[3px] md:border-b-[5px] border-b-empire-yellow cursor-pointer md:cursor-default">
                <h3 class="font-archivo-narrow font-semibold text-[20px] md:text-[24px] leading-[50px] md:leading-[72px] text-empire-yellow px-4 md:px-6">
                  {{ $t('categoryPage.price') }}
                </h3>
                <!-- Yellow arrow to expand/collapse (mobile only) -->
                <div class="md:hidden flex items-center pr-4">

                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" :class="{ 'transform rotate-180': !isPriceExpanded }">
                    <path d="M6 9L12 15L18 9" stroke="#FFDD00" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </div>
              </div>
              <div v-show="isPriceExpanded" class="border border-[#FAFAFA] p-4">
                <div class="flex justify-between items-center mb-2">
                  <span class="font-archivo text-sm">
                    {{ currencySymbol }}{{ priceRange[0] }}
                  </span>
                  <span class="font-archivo text-sm">
                    {{ currencySymbol }}{{ priceRange[1] }}
                  </span>
                </div>
                <div class="relative w-full h-8">
                  <div class="absolute w-full h-2 bg-[#E6E6E6] rounded-full top-3"></div>
                  <div
                    class="absolute h-2 bg-black rounded-full top-3"
                    :style="{ left: (priceRange[0] / maxPrice) * 100 + '%', right: 100 - (priceRange[1] / maxPrice) * 100 + '%' }"
                  ></div>
                  <input
                    type="range"
                    :min="0"
                    :max="maxPrice"
                    v-model.number="priceRange[0]"
                    class="absolute w-full h-2 appearance-none bg-transparent pointer-events-none"
                    @mousedown="handleRangeStart"
                    @mouseup="handleRangeEnd"
                    @touchstart="handleRangeStart"
                    @touchend="handleRangeEnd"
                  >
                  <input
                    type="range"
                    :min="0"
                    :max="maxPrice"
                    v-model.number="priceRange[1]"
                    class="absolute w-full h-2 appearance-none bg-transparent pointer-events-none"
                    @mousedown="handleRangeStart"
                    @mouseup="handleRangeEnd"
                    @touchstart="handleRangeStart"
                    @touchend="handleRangeEnd"
                  >
                </div>
              </div>
            </div>

            <!-- Dynamic Filters by Root Characteristic (Mobile Inline) -->
            <DynamicFilterMobile
              v-for="rootFilter in displayedRootFiltersWithCounts"
              :key="rootFilter.id"
              :root-filter="rootFilter"
              :selected-filters="selectedFilters"
              :initial-expanded="true"
              :disabled="isApplyingFilters"
              @update:selectedFilters="selectedFilters = $event"
            />
          </div>



          <!-- Pagination (Top) -->
          <div class="hidden md:flex flex-col items-center mb-6 w-full gap-4">
            <div class="flex justify-between items-center w-full max-w-[1036px] h-10 gap-6">
              <!-- Navigation (Prev, Numbers, Next) -->
              <div class="flex items-center gap-4">
                <!-- Prev (ícone) -->
                <button
                  class="w-12 h-10 bg-[#F9F9FB] rounded-lg flex items-center justify-center"
                  :disabled="currentPage === 1"
                  @click="handlePageChange(currentPage - 1)"
                  aria-label="Previous page"
                >
                  <svg class="w-5 h-5" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12.5 15L7.5 10L12.5 5" stroke="#1E1E1E" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </button>

                <!-- Números -->
                <div class="flex items-center gap-2">
                  <button
                    v-for="page in displayedPages"
                    :key="page"
                    class="w-10 h-10 flex items-center justify-center rounded-lg font-inter font-medium text-base"
                    :class="page === currentPage ? 'bg-empire-yellow text-black' : 'bg-[#F9F9FB] text-black'"
                    @click="handlePageChange(page)"
                  >
                    {{ page }}
                  </button>
                </div>

                <!-- Next (ícone) -->
                <button
                  class="w-12 h-10 bg-[#F9F9FB] rounded-lg flex items-center justify-center"
                  :disabled="currentPage === totalPages"
                  @click="handlePageChange(currentPage + 1)"
                  aria-label="Next page"
                >
                  <svg class="w-5 h-5" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M7.5 15L12.5 10L7.5 5" stroke="#1E1E1E" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </button>
              </div>

              <!-- Itens por página (direita) -->
              <div class="hidden md:flex items-center gap-2">
                <button
                  v-for="n in itemsPerPageOptions"
                  :key="'ipp-top-'+n"
                  @click="setItemsPerPage(n)"
                  class="w-12 h-10 rounded-lg flex items-center justify-center font-inter font-medium text-base"
                  :class="itemsPerPage === n ? 'bg-empire-yellow text-black' : 'bg-[#F9F9FB] text-black'"
                >
                  {{ n }}
                </button>
              </div>
            </div>
          </div>

          <!-- No Results (centered message) -->
          <template v-if="!loading && totalItems === 0">
            <div class="w-full flex justify-center items-center py-8">
              <div class="flex flex-col items-center w-[1036px] h-[236px] px-0">
                <div class="w-full h-[125px] flex items-center justify-center">
                  <h2 class="font-archivo-narrow font-semibold text-[45px] leading-[50px] text-black/70 text-center">
                    {{ $t('categoryPage.noResultsDetailed', { terms: displayedSearchText }) }}
                  </h2>
                </div>
              </div>
            </div>

            <!-- Suggestions -->
            <template v-if="suggestedProducts.length">
              <!-- Grid Mode -->
              <template v-if="displayMode === 'grid'">
                <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-0.5 md:gap-1">
                  <div
                    v-for="product in suggestedProducts"
                    :key="`sug-${product.id}`"
                    :class="[
                      'flex flex-col bg-white border border-[#FAFAFA]',
                      showPrices ? 'min-h-[250px] md:min-h-[280px] md:h-[300px]' : 'min-h-[230px] md:min-h-[260px] md:h-[280px]'
                    ]"
                  >
                    <div class="flex flex-col flex-grow cursor-pointer" @click="navigateToProduct(product.id)">
                      <img :src="getProductImage(product)" :alt="product.name" class="w-[90%] h-[110px] md:h-[140px] object-contain object-center mx-auto pt-1 md:pt-0" @error="handleImageError" />
                      <div class="p-1 pt-1 pb-0 md:p-1 md:pb-0 flex flex-col h-full">
                        <div class="h-[32px] md:h-[36px] overflow-hidden">
                          <p class="font-archivo font-bold text-[12px] md:text-[13px] leading-[16px] md:leading-[18px] text-black/90 line-clamp-2 mb-0 text-center">{{ product.description || '-' }}</p>
                        </div>
                        <h3 class="font-archivo-narrow text-[12px] md:text-[14px] leading-[14px] md:leading-[16px] line-clamp-1 truncate mt-0 mb-0 product-name uppercase">{{ product.name }}</h3>
                        <div class="mt-2 w-full mb-0 pb-0">
                          <PriceSkeleton v-if="showPrices && pricesLoading" size="medium" />
                          <p v-else-if="showPrices" class="font-archivo-narrow font-semibold text-[18px] md:text-[20px] leading-[20px] md:leading-[22px] mt-0 mb-0 text-center md:text-center">{{ formatPrice(product.price, product) }}</p>
                          <div v-else class="mt-0"></div>
                          <div @click.stop class="grid-mobile-actions w-full mt-0 -mb-1">
                            <ProductQuantitySelector @add-to-cart="(quantity) => handleAddToCart(product, quantity)" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </template>

              <!-- List Mode -->
              <template v-else>
                <div class="flex flex-col items-center gap-3">
                  <div
                    v-for="product in suggestedProducts"
                    :key="'list-sug-' + product.id"
                    class="w-full max-w-[1036px] bg-white border border-[#EBEBEB] transform rotate-[0.21deg]"
                  >
                    <!-- ITEM-BOX (Mobile - formato compacto) -->
                    <div class="md:hidden flex flex-row items-center p-0 w-full max-w-[380px] h-[108.93px] bg-white transform rotate-[0.21deg] mx-auto cursor-pointer" @click="navigateToProduct(product.id)">
                      <!-- image -->
                      <img :src="getProductImage(product)" :alt="product.name" class="w-[100px] h-[92px] object-contain flex-none" @error="handleImageError" />

                      <!-- Frame 67: direita -->
                      <div class="flex flex-row items-start gap-1 flex-1 h-[108.93px] min-w-0">
                        <!-- Frame 66: texto -->
                        <div class="flex flex-col items-start flex-1 min-w-0">
                          <!-- Frame 64: title + description -->
                          <div class="flex flex-col items-start flex-1 min-w-0 gap-[1px]">
                            <h3 class="font-archivo-narrow font-semibold text-[15px] leading-[18px] uppercase text-black/70 w-full block line-clamp-2 overflow-hidden">{{ product.name }}</h3>
                            <p class="font-archivo font-medium text-[12px] leading-[16px] text-black/70 line-clamp-3 overflow-hidden">{{ product.description }}</p>
                          </div>
                          <!-- Frame 65: price -->
                          <div class="flex flex-col w-auto">
                            <PriceSkeleton v-if="showPrices && pricesLoading" size="small" />
                            <p v-else-if="showPrices" class="font-archivo-narrow font-semibold text-[16px] leading-[20px] text-black">{{ formatPrice(product.price, product) }}</p>
                          </div>
                        </div>

                        <!-- Frame 88: actions -->
                        <div class="list-mobile-actions flex flex-col items-end ml-auto pr-0 gap-1 w-[90px] h-[108.93px] overflow-hidden shrink-0 mt-[3px]" @click.stop>
                          <ProductQuantitySelectorMobileList @add-to-cart="(quantity) => handleAddToCart(product, quantity)" />
                        </div>
                      </div>
                    </div>

                    <!-- ITEM-BOX (Desktop+) -->
                    <div class="hidden md:block">
                      <div class="flex flex-col md:flex-row justify-start items-center md:items-start p-3 min-h-[220px] gap-3 cursor-pointer" @click="navigateToProduct(product.id)">
                        <!-- image -->
                        <img :src="getProductImage(product)" :alt="product.name" class="w-[200px] h-[180px] object-contain mx-auto md:mx-0" @error="handleImageError" />

                        <!-- Center Column: text -->
                        <div class="flex flex-col gap-3 w-[340px] md:w-[360px] pt-2 md:pt-3">
                          <div class="flex flex-col gap-4">
                            <div class="flex flex-col">
                              <h3 class="font-archivo-narrow font-semibold text-[22px] leading-[26px] text-black/70 uppercase">{{ product.name }}</h3>
                              <p class="font-archivo font-medium text-[16px] leading-[20px] text-black/70 break-words line-clamp-2 mt-[1px]">{{ product.description }}</p>
                            </div>
                            <div class="flex flex-col">
                              <PriceSkeleton v-if="showPrices && pricesLoading" size="large" />
                              <p v-else-if="showPrices" class="font-archivo-narrow font-semibold text-[24px] leading-[28px] text-black">{{ formatPrice(product.price, product) }}</p>
                            </div>
                          </div>
                        </div>

                        <!-- Right Column: Controls (vertical) -->
                        <div class="list-actions flex flex-col items-stretch w-[220px] md:w-[280px] md:self-center">
                          <div @click.stop>
                            <ProductQuantitySelector customHeight="72px" @add-to-cart="(quantity) => handleAddToCart(product, quantity)" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </template>
            </template>

          </template>

          <!-- Products: Grid or List -->
          <!-- Products: Grid or List -->
          <template v-if="displayMode === 'grid'">
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-0.5 md:gap-1">
              <div v-if="loading && filteredProducts.length === 0" class="col-span-full flex justify-center items-center py-12">
                <div class="loader-container">
                  <div class="loader-spinner"></div>
                  <div class="loader-text">{{ $t('common.loading') }}...</div>
                </div>
              </div>
              <div v-else-if="error" class="col-span-full text-red-500">
                {{ error }}
              </div>
              <template v-else>
                <div
                  v-for="product in filteredProducts"
                  :key="product.id"
                  :class="[
                    'flex flex-col bg-white border border-[#FAFAFA]',
                    showPrices ? 'min-h-[250px] md:min-h-[280px] md:h-[300px]' : 'min-h-[230px] md:min-h-[260px] md:h-[280px]'
                  ]"
                >
                  <div class="flex flex-col flex-grow cursor-pointer" @click="navigateToProduct(product.id)">
                    <img :src="getProductImage(product)" :alt="product.name" class="w-[90%] h-[110px] md:h-[140px] object-contain object-center mx-auto pt-1 md:pt-0" @error="handleImageError" />
                    <div class="p-1 pt-1 pb-0 md:p-1 md:pb-0 flex flex-col h-full">
                      <div class="h-[32px] md:h-[36px] overflow-hidden">
                        <p class="font-archivo font-bold text-[12px] md:text-[13px] leading-[16px] md:leading-[18px] text-black/90 line-clamp-2 mb-0 text-center">{{ product.description || '-' }}</p>
                      </div>
                      <h3 class="font-archivo-narrow text-[12px] md:text-[14px] leading-[14px] md:leading-[16px] line-clamp-1 truncate mt-0 mb-0 product-name uppercase">{{ product.name }}</h3>
                      <div class="mt-2 w-full mb-0 pb-0">
                        <PriceSkeleton v-if="showPrices && pricesLoading" size="medium" />
                        <p v-else-if="showPrices" class="font-archivo-narrow font-semibold text-[18px] md:text-[20px] leading-[20px] md:leading-[22px] mt-0 mb-0 text-center md:text-center">{{ formatPrice(product.price, product) }}</p>
                        <div v-else class="mt-0"></div>
                        <div @click.stop class="grid-mobile-actions w-full mt-0 -mb-1">
                          <ProductQuantitySelector @add-to-cart="(quantity) => handleAddToCart(product, quantity)" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </template>
            </div>
          </template>

          <!-- List (1 column on all breakpoints) -->
          <template v-else>
            <div class="flex flex-col items-center gap-3">
              <div v-if="loading && filteredProducts.length === 0" class="w-full max-w-[1036px] flex justify-center items-center py-12">
                <div class="loader-container">
                  <div class="loader-spinner"></div>
                  <div class="loader-text">{{ $t('common.loading') }}...</div>
                </div>
              </div>
              <div v-else-if="error" class="w-full max-w-[1036px] text-red-500">
                {{ error }}
              </div>
              <template v-else>
                <div
                  v-for="product in filteredProducts"
                  :key="'list-' + product.id"
                  class="w-full max-w-[1036px] bg-white border border-[#EBEBEB] transform rotate-[0.21deg]"
                >
                  <!-- ITEM-BOX -->
                      <!-- ITEM-BOX (Mobile - formato compacto) -->
                      <div class="md:hidden flex flex-row items-center p-0 w-full max-w-[380px] h-[108.93px] bg-white transform rotate-[0.21deg] mx-auto cursor-pointer" @click="navigateToProduct(product.id)">
                        <!-- image -->
                        <img :src="getProductImage(product)" :alt="product.name" class="w-[100px] h-[92px] object-contain flex-none" @error="handleImageError" />

                        <!-- Frame 67: direita -->
                        <div class="flex flex-row items-start gap-1 flex-1 h-[108.93px] min-w-0">
                          <!-- Frame 66: texto -->
                          <div class="flex flex-col items-start flex-1 min-w-0">
                            <!-- Frame 64: title + description -->
                            <div class="flex flex-col items-start flex-1 min-w-0 gap-[1px]">
                              <h3 class="font-archivo-narrow font-semibold text-[15px] leading-[18px] uppercase text-black/70 w-full block line-clamp-2 overflow-hidden">{{ product.name }}</h3>
                              <p class="font-archivo font-medium text-[12px] leading-[16px] text-black/70 line-clamp-3 overflow-hidden">{{ product.description }}</p>
                            </div>
                            <!-- Frame 65: price -->
                            <div class="flex flex-col w-auto">
                              <PriceSkeleton v-if="showPrices && pricesLoading" size="small" />
                              <p v-else-if="showPrices" class="font-archivo-narrow font-semibold text-[16px] leading-[20px] text-black">{{ formatPrice(product.price) }}</p>
                            </div>
                          </div>

                          <!-- Frame 88: actions -->
                          <div class="list-mobile-actions flex flex-col items-end ml-auto pr-0 gap-1 w-[90px] h-[108.93px] overflow-hidden shrink-0 mt-[3px]" @click.stop>
                            <ProductQuantitySelectorMobileList @add-to-cart="(quantity) => handleAddToCart(product, quantity)" />
                          </div>
                        </div>
                      </div>

                      <!-- ITEM-BOX (Desktop+) -->
                      <div class="hidden md:block">

                  <div class="flex flex-col md:flex-row justify-start items-center md:items-start p-3 min-h-[220px] gap-3 cursor-pointer" @click="navigateToProduct(product.id)">
                    <!-- image -->
                    <img :src="getProductImage(product)" :alt="product.name" class="w-[200px] h-[180px] object-contain mx-auto md:mx-0" @error="handleImageError" />

                    <!-- Center Column: text -->
                    <div class="flex flex-col gap-3 w-[340px] md:w-[360px] pt-2 md:pt-3">
                      <div class="flex flex-col gap-4">
                        <div class="flex flex-col">
                          <h3 class="font-archivo-narrow font-semibold text-[22px] leading-[26px] text-black/70 uppercase">{{ product.name }}</h3>
                          <p class="font-archivo font-medium text-[16px] leading-[20px] text-black/70 break-words line-clamp-2 mt-[1px]">{{ product.description }}</p>
                        </div>
                        <div class="flex flex-col">
                          <PriceSkeleton v-if="showPrices && pricesLoading" size="large" />
                          <p v-else-if="showPrices" class="font-archivo-narrow font-semibold text-[24px] leading-[28px] text-black">{{ formatPrice(product.price, product) }}</p>
                        </div>
                      </div>
                    </div>

                    <!-- Right Column: Controls (vertical) -->
                    <div class="list-actions flex flex-col items-stretch w-[220px] md:w-[280px] md:self-center">
                      <div @click.stop>
                        <ProductQuantitySelector customHeight="72px" @add-to-cart="(quantity) => handleAddToCart(product, quantity)" />
                      </div>
                    </div>
                  </div>
                </div>
                </div>

              </template>
            </div>
          </template>


          <!-- Pagination (Mobile) - Centered and with the Figma layout -->
          <div class="md:hidden flex flex-col items-center p-0 gap-6 w-full max-w-[380px] mx-auto mt-12 mb-16">
            <!-- Frame 125 -->
            <div class="flex flex-col items-center p-0 gap-4 w-full max-w-[332px]">
              <!-- Navigation -->
              <div class="flex flex-row justify-center items-center p-0 gap-4 w-full max-w-[332px] mx-auto">
                <!-- Prev -->
                <button
                  class="relative flex flex-row justify-center items-center px-[14px] py-2 w-12 h-10 bg-[#F9F9FB] rounded-lg"
                  :disabled="currentPage === 1"
                  @click="handlePageChange(currentPage - 1)"
                  aria-label="Previous page"
                >
                  <svg class="w-5 h-5" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12.5 15L7.5 10L12.5 5" stroke="#1E1E1E" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </button>

                <!-- Numbers -->
                <button
                  v-for="page in displayedPages"
                  :key="'m-page-'+page"
                  class="flex flex-row justify-center items-center px-[14px] w-9 h-10 rounded-lg font-inter font-medium text-[16px] leading-[24px]"
                  :class="page === currentPage ? 'bg-empire-yellow text-black' : 'bg-[#F9F9FB] text-black'"
                  @click="handlePageChange(page)"
                >
                  {{ page }}
                </button>

                <!-- Next -->
                <button
                  class="relative flex flex-row justify-center items-center px-[14px] py-2 w-12 h-10 bg-[#F9F9FB] rounded-lg"
                  :disabled="currentPage === totalPages"
                  @click="handlePageChange(currentPage + 1)"
                  aria-label="Next page"
                >
                  <svg class="w-5 h-5" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M7.5 15L12.5 10L7.5 5" stroke="#1E1E1E" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </button>
              </div>

              <!-- 1-12 of X items -->
              <div class="w-full text-center">
                <span class="font-inter font-normal text-[16px] leading-[24px] text-black">
                  {{ totalItems === 0
                    ? $t('categoryPage.noResults')
                    : $t('categoryPage.itemsCount', {
                        start: itemRange.start,
                        end: itemRange.end,
                        total: totalItems
                      })
                  }}
                </span>
              </div>
            </div>

            <!-- Items per page (Mobile) -->
            <div class="flex flex-row justify-center items-center gap-4 w-full max-w-[332px] mx-auto">
              <button
                v-for="n in itemsPerPageOptions"
                :key="'m-ipp-'+n"
                @click="setItemsPerPage(n)"
                class="w-12 h-10 rounded-lg flex items-center justify-center font-inter font-medium text-[16px] leading-[24px]"
                :class="itemsPerPage === n ? 'bg-empire-yellow text-black' : 'bg-[#F9F9FB] text-black'"
              >
                {{ n }}
              </button>
            </div>

          </div>

          <!-- Pagination (Desktop/Tablet) -->

          <div class="hidden md:flex flex-col items-center mt-12 mb-24 w-full gap-4">
            <div class="flex justify-between items-center w-full max-w-[1036px] h-10 gap-6">
              <!-- Navigation (Prev, Numbers, Next) -->
              <div class="flex items-center gap-4">
                <!-- Prev (ícone) -->
                <button
                  class="w-12 h-10 bg-[#F9F9FB] rounded-lg flex items-center justify-center"
                  :disabled="currentPage === 1"
                  @click="handlePageChange(currentPage - 1)"
                  aria-label="Previous page"
                >
                  <svg class="w-5 h-5" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12.5 15L7.5 10L12.5 5" stroke="#1E1E1E" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </button>

                <!-- Números -->
                <div class="flex items-center gap-2">
                  <button
                    v-for="page in displayedPages"
                    :key="page"
                    class="w-10 h-10 flex items-center justify-center rounded-lg font-inter font-medium text-base"
                    :class="page === currentPage ? 'bg-empire-yellow text-black' : 'bg-[#F9F9FB] text-black'"
                    @click="handlePageChange(page)"
                  >
                    {{ page }}
                  </button>
                </div>

                <!-- Next (ícone) -->
                <button
                  class="w-12 h-10 bg-[#F9F9FB] rounded-lg flex items-center justify-center"
                  :disabled="currentPage === totalPages"
                  @click="handlePageChange(currentPage + 1)"
                  aria-label="Next page"
                >
                  <svg class="w-5 h-5" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M7.5 15L12.5 10L7.5 5" stroke="#1E1E1E" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </button>
              </div>

              <!-- Itens por página (direita) -->
              <div class="hidden md:flex items-center gap-2">
                <button
                  v-for="n in itemsPerPageOptions"
                  :key="'ipp-'+n"
                  @click="setItemsPerPage(n)"
                  class="w-12 h-10 rounded-lg flex items-center justify-center font-inter font-medium text-base"
                  :class="itemsPerPage === n ? 'bg-empire-yellow text-black' : 'bg-[#F9F9FB] text-black'"
                >
                  {{ n }}
                </button>
              </div>
            </div>

            <!-- Item counter in the pagination -->
            <div class="flex justify-center">
              <span class="font-inter text-sm md:text-base">
                {{ totalItems === 0
                  ? $t('categoryPage.noResults')
                  : $t('categoryPage.itemsCount', {
                      start: itemRange.start,
                      end: itemRange.end,
                      total: totalItems
                    })
                }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Mobile Filters Modal -->
    <div
      v-if="showMobileFilters"
      class="fixed inset-0 bg-black bg-opacity-50 z-50 lg:hidden"
      @click="showMobileFilters = false"
    >
      <div
        class="absolute right-0 top-0 bottom-0 w-[300px] bg-white p-4 overflow-y-auto"
        @click.stop
      >
        <div class="flex justify-between items-center mb-3">
          <h2 class="font-archivo-narrow font-semibold text-[18px] leading-[22px]">{{ $t('categoryPage.filters') }}</h2>
          <button
            @click="showMobileFilters = false"
            class="p-2"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6l12 12" stroke="#000" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </div>

        <!-- Mobile Filters Content -->
        <div class="space-y-2">
          <!-- Price Filter (visible only when showPrices is true) -->
          <!-- Search (mobile) -->
          <div class="mb-3">
            <div class="flex items-center justify-between gap-0 border-2 border-black/70 h-16 pl-2 pr-0 w-full">
              <input
                v-model="searchQuerySidebar"
                @keydown.enter="goToSearch"
                type="text"
                :placeholder="$t('categoryPage.searchPlaceholderShort')"
                class="flex-1 px-2 outline-none font-archivo text-[15px] text-black/70 bg-transparent"
              />
              <button
                @click="goToSearch"
                type="button"
                class="w-[58px] h-full bg-empire-yellow flex items-center justify-center"
                :aria-label="$t('header.search')"
              >
                <svg class="w-6 h-6 md:w-8 md:h-8" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="11" cy="11" r="7" stroke="#000" stroke-width="2" />
                  <line x1="16.65" y1="16.65" x2="21" y2="21" stroke="#000" stroke-width="2" stroke-linecap="round" />
                </svg>
              </button>
            </div>
          </div>

          <div v-if="showPrices && showPriceFilter">
            <h3 class="font-archivo-narrow font-semibold text-sm mb-1">{{ $t('categoryPage.priceRange') }}</h3>
            <div class="space-y-2">
              <!-- Price slider using the native range input -->
              <div class="px-1 py-2">
                <div class="mb-2 flex justify-between">
                  <span class="font-archivo text-sm">{{ showPrices ? `${currencySymbol}${priceRange[0]}` : '' }}</span>
                  <span class="font-archivo text-sm">{{ showPrices ? `${currencySymbol}${priceRange[1]}` : '' }}</span>
                </div>
                <div class="relative h-8 mt-2 mb-2">
                  <input

                    type="range"
                    :min="0"
                    :max="maxPrice"
                    :value="priceRange[1]"
                    @input="updateMaxPriceSlider"
                    @change="handlePriceRangeChange"
                    class="absolute w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                    style="z-index: 1;"
                  />
                  <input
                    type="range"
                    :min="0"
                    :max="maxPrice"
                    :value="priceRange[0]"
                    @input="updateMinPrice"
                    @change="handlePriceRangeChange"
                    class="absolute w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                    style="z-index: 2;"
                  />
                </div>
              </div>
            </div>
          </div>

          <!-- FILIALE (mobile) - HIDDEN -->
          <div v-if="false">
            <div class="flex items-center justify-between w-full h-[50px] bg-black border-b-[3px] border-b-empire-yellow cursor-pointer" @click="isBranchesExpanded = !isBranchesExpanded">
              <h3 class="font-archivo-narrow font-semibold text-white px-4">{{ $t('categoryPage.branches') }}</h3>
              <div class="flex items-center">

                <svg class="w-5 h-5 text-white mr-4 transform" :class="{ 'rotate-180': isBranchesExpanded }" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                </svg>
              </div>
            </div>
            <div v-show="isBranchesExpanded" class="border border-[#FAFAFA] p-4 space-y-2">
              <div v-if="branchOptions.length === 0" class="text-sm text-black/50 font-archivo">
                {{ $t('categoryPage.noBranches') }}
              </div>
              <label v-for="(branch, idx) in branchOptions" :key="'m-branch-'+idx" class="flex items-center gap-3 cursor-pointer select-none">
                <input type="checkbox" v-model="selectedBranches" :value="branch" class="w-4 h-4 border border-black/40" />
                <span class="font-archivo text-[15px] text-black/70">{{ branch }}</span>
              </label>
            </div>
          </div>

          <!-- FILTRE PER (mobile) - HIDDEN -->
          <div v-if="false">
            <div class="flex items-center justify-between w-full h-[50px] bg-black border-b-[3px] border-b-empire-yellow cursor-pointer" @click="isFilterPerExpanded = !isFilterPerExpanded">
              <h3 class="font-archivo-narrow font-semibold text-white px-4">{{ $t('categoryPage.filterBy') }}</h3>
              <div class="flex items-center">

                <svg class="w-5 h-5 text-white mr-4 transform" :class="{ 'rotate-180': isFilterPerExpanded }" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                </svg>
              </div>
            </div>
            <div v-show="isFilterPerExpanded" class="border border-[#FAFAFA] p-4 space-y-2">
              <div v-if="filterPerOptions.length === 0" class="text-sm text-black/50 font-archivo">
                {{ $t('categoryPage.noAdditionalFilters') }}
              </div>
              <label v-for="(tag, idx) in filterPerOptions" :key="'m-fp-'+idx" class="flex items-center gap-3 cursor-pointer select-none">
                <input type="checkbox" v-model="selectedFilterPer" :value="tag" class="w-4 h-4 border border-black/40" />
                <span class="font-archivo text-[15px] text-black/70">{{ tag }}</span>
              </label>
            </div>
          </div>

          <!-- Dynamic Filters by Root Characteristic (Mobile Modal) -->
          <DynamicFilterModal
            v-for="rootFilter in displayedRootFiltersWithCounts"
            :key="rootFilter.id"
            :root-filter="rootFilter"
            :selected-filters="selectedFilters"
            :initial-expanded="true"
            :disabled="isApplyingFilters"
            @update:selectedFilters="selectedFilters = $event"
          />
        </div>
      </div>
    </div>
    <!-- Toast Message -->
    <div
      v-if="showToast"
      class="fixed bottom-4 right-4 bg-black text-empire-yellow px-6 py-4 rounded-md shadow-lg z-50 transition-opacity duration-300"
      :class="{ 'opacity-0': !showToast, 'opacity-100': showToast }"
    >
      <p class="font-archivo-narrow text-lg">
        {{ $t('cart.productAdded') }}
      </p>
    </div>

  </div>


</template>

<style scoped>
/* Reduce spacing between the card elements */
.grid-cols-4 {


  gap: 0.5rem !important;
}

/* Style for line-clamp */
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.1;
  word-break: break-word;
}

/* 3-line clamp utility for mobile description */
.line-clamp-3 {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}


/* Custom style for the product name with extra thin weight */
.product-name {
  font-weight: 100 !important; /* Forces the thinnest possible weight */
  opacity: 0.7 !important; /* Lowers the opacity further so it looks thinner */
  letter-spacing: 0.03em !important; /* Increases the letter spacing further */
  color: rgba(0, 0, 0, 0.6) !important; /* Lighter color so it looks thinner */
  transform: scale(0.98, 1) !important; /* Comprime ligeiramente na horizontal */
  text-align: center !important; /* Centraliza o texto */
}
</style>

<script setup>
/* eslint-disable */
import { ref, onMounted, watch, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { productService, filterProductsByDisplayedPriceRange } from '@/services/productService'
import { categoryService } from '@/services/categoryService'
import { settingsService } from '@/services/settingsService'
import { imageService } from '@/services/imageService'
import ProductQuantitySelector from '@/components/product/ProductQuantitySelector.vue'
import ProductQuantitySelectorMobileList from '@/components/product/ProductQuantitySelectorMobileList.vue'
import PriceSkeleton from '@/components/product/PriceSkeleton.vue'
import CategoryTree from '@/components/category/CategoryTree.vue'
import DynamicFilter from '@/components/category/DynamicFilter.vue'
import DynamicFilterMobile from '@/components/category/DynamicFilterMobile.vue'
import DynamicFilterModal from '@/components/category/DynamicFilterModal.vue'
import { PLACEHOLDER_IMAGE_PATH } from '@/services/imageConstants'
import { filterService } from '@/services/filterService'
import { debounce } from 'lodash'
import { useCartStore } from '@/stores/cartStore'
import { useFinancialTogglesStore } from '@/stores/financialTogglesStore'
import { useI18n } from 'vue-i18n'
import { productCharacteristicsService } from '@/services/productCharacteristicsService'
import { getDefaultUnit } from '@/utils/unitUtils'
import { searchAnalyticsService } from '@/services/searchAnalyticsService'
import { useDynamicSearch } from '@/composables/useDynamicSearch'
import searchService from '@/services/searchService'
import { locationService } from '@/services/locationService'
import eventBus from '@/utils/eventBus'
import { getProductUrlWithDescription } from '@/utils/urlUtils'
// Removing the VueSlider import since it is not installed

const route = useRoute()
const router = useRouter()
const { locale, t } = useI18n() // Adding it here at the top with the other composables

// Ref to store the selected location
const selectedLocation = ref(null)

// Dynamic search composable
const {
  isLoading: dynamicSearchLoading,
  updateSearchQuery: updateDynamicSearch,
  executeSearch: runDynamicSearch,
  updateSelectedFilters: setDynamicFilters,
  categories: dynamicCategories,
  rootFiltersWithProducts,
  setInStockOnly
} = useDynamicSearch()

// Removendo registro do componente VueSlider
const currentPage = ref(1)
const totalPages = ref(1)
const products = ref([])
const loading = ref(true)
const loadingCategories = ref(true)
const pricesLoading = ref(false) // State to control the price loading
const error = ref(null)
// We no longer need the categories variable, since we use categoryTree directly
const categoryTree = ref([])
// Category list coming only from the CHIPS (ignores category) to feed the tree
const chipsOnlyCategories = ref([])

// Source for the category tree: uses chipsOnlyCategories when available, otherwise the default dynamic search result
const categoriesForTree = computed(() => {
  return (chipsOnlyCategories.value && chipsOnlyCategories.value.length > 0)
    ? chipsOnlyCategories.value
    : dynamicCategories.value
})

const selectedCategory = ref(null)
const isInternalRouteChange = ref(false) // Flag to avoid a loop in the route watcher


// Base root filters (must be declared before the computeds that depend on it)
const rootFilters = ref([])

// Selected dynamic filters (must be declared before the computeds that use them)
const selectedFilters = ref([])


// New states for the added elements (Figma)
const branchOptions = ref([
  'Quebec Les Saules, QC Aisle 17',
  'Montréal Saint-Laurent, QC Aisle 4',
  'Laval Chomedey, QC Aisle 12'
]) // mock for the layout
const isBranchesExpanded = ref(true)
const isFilterPerExpanded = ref(true)

const selectedBranches = ref([])
const filterPerOptions = ref([
  'Fin de série (11)',
  'Liquidation (12)',
  'Nouveau (4)'
]) // mock for the layout
const selectedFilterPer = ref([])
const displayMode = ref('grid')


const onlyInStock = ref(false)

// Keeps the "Only in stock" filter in sync with the dynamic search and the listing
watch(onlyInStock, async (val) => {
  try {
    setInStockOnly(val)
    const chips = (searchChips.value || []).map(x => (x || '').trim()).filter(Boolean)
    const dynamicSearchText = chips.join(' ')
    updateDynamicSearch(dynamicSearchText, selectedCategory.value ?? null)
    await runDynamicSearch(selectedCategory.value ?? null)
    currentPage.value = 1
    await fetchFilteredProducts()
  } catch (e) {
    console.error('Erro ao aplicar filtro Only in stock:', e)
  }
})

// Initializes the value in the dynamic search composable
setInStockOnly(onlyInStock.value)

// Search tokens displayed below the main search (while typing)
const searchChips = ref([])

const addChipsFromInput = () => {
  const raw = (searchQuerySidebar.value || '').trim()
  if (!raw) return
  const parts = raw.split(/\s+/).filter(Boolean)
  const existing = new Set(searchChips.value.map(x => (x || '').toLowerCase()))
  const beforeLen = searchChips.value.length
  for (const p of parts) {
    const k = (p || '').toLowerCase()
    if (!existing.has(k)) {
      searchChips.value.push(p)
      existing.add(k)
    }
  }
  // If new chips were added, unselect the category so it does not combine automatically
  if (searchChips.value.length > beforeLen) {
    selectedCategory.value = null
  }
  searchQuerySidebar.value = ''
}

// Selected filters shown below the sidebar search so they can be removed from there
const selectedFilterChips = computed(() => {
  const byId = new Map()
  ;(rootFilters.value || []).forEach(root => {
    ;(root.children || []).forEach(child => {
      byId.set(String(child.id), { id: child.id, name: child.name, rootName: root.name })
    })
  })
  return (selectedFilters.value || [])
    .map(id => byId.get(String(id)))
    .filter(Boolean)
})

const removeSelectedFilter = (filterId) => {
  selectedFilters.value = selectedFilters.value.filter(id => String(id) !== String(filterId))
}

const clearSelectedFilters = () => {
  selectedFilters.value = []
}

const removeKeyword = (kw) => {
  const target = (kw || '').toLowerCase()
  searchChips.value = searchChips.value.filter(t => (t || '').toLowerCase() !== target)

  // Updates dynamic filters (dynamic search uses a space between terms)
  const chips = (searchChips.value || []).map(x => (x || '').trim()).filter(Boolean)
  const dynamicSearchText = chips.join(' ')
  updateDynamicSearch(dynamicSearchText, selectedCategory.value ?? null)

  // Runs the dynamic search immediately to refresh counts without waiting for the debounce
  runDynamicSearch(selectedCategory.value ?? null)
  currentPage.value = 1

  // If there are no chips left, remove the keyword parameter from the URL
  if (chips.length === 0 && route.query.keyword) {
    const newQuery = { ...route.query }
    delete newQuery.keyword
    isInternalRouteChange.value = true
    router.replace({ path: route.path, query: newQuery })
    setTimeout(() => {
      isInternalRouteChange.value = false
    }, 100)
  } else if (chips.length > 0) {
    // Updates the keyword in the URL with the remaining chips
    const newKeyword = chips.join(' ')
    if (route.query.keyword !== newKeyword) {
      isInternalRouteChange.value = true
      router.replace({ path: route.path, query: { ...route.query, keyword: newKeyword } })
      setTimeout(() => {
        isInternalRouteChange.value = false
      }, 100)
    }
  }

  fetchFilteredProducts()
}

const searchQuerySidebar = ref('')

// REMOVED: Watch that triggered a search on every chip change
// The search is now only triggered explicitly on Enter or when removing a chip

// Computed to merge categories with the dynamic search counts
const categoryTreeWithCounts = computed(() => {
  // Dynamic mode only when there are CHIPS, selected FILTERS or an active stock filter
  const hasChips = searchChips.value.length > 0
  const hasSelectedCategory = !!selectedCategory.value
  const hasSelectedFilters = Array.isArray(selectedFilters.value) && selectedFilters.value.length > 0
  const hasStockFilter = onlyInStock.value === true
  const inDynamicMode = hasChips || hasSelectedFilters || hasStockFilter

  // Map of counts per category
  const categoriesMap = new Map()

  // Function to map counts from any given tree
  const addToMap = (node, useBaseCounts = false) => {
    if (!node) return
    const countValue = useBaseCounts ? (node.count || 0) : (node.productCount || 0)
    categoriesMap.set(String(node.id), countValue)
    if (node.children && node.children.length > 0) {
      node.children.forEach(child => addToMap(child, useBaseCounts))
    }
  }

  if (inDynamicMode) {
    // Uses specific categories for the TREE (chips-only when available)
    categoriesForTree.value.forEach(node => addToMap(node, false))
  } else {
    // No chips/filters: uses the BASE counts (count) from the original tree
    categoryTree.value.forEach(node => addToMap(node, true))
  }

  if (hasSelectedCategory) {
    console.log('📊 CategoriesMap completo:', Array.from(categoriesMap.entries()))
  }

  // When a category is selected: show only the selected branch
  // - If the selected one is a ROOT: show only the root and its children
  // - If the selected one is a CHILD (not root): show only it (no siblings, no other nodes)
  const baseTree = (() => {
    if (hasSelectedCategory) {
      // Find the selected node in the full tree
      const selectedNode = findCategoryById(categoryTree.value, selectedCategory.value)

      if (selectedNode) {
        // Considered a root when it has no parent_id (or a falsy parent_id)
        const isRoot = !selectedNode.parent_id

        if (isRoot) {
          // Only the selected root with its full hierarchy
          const rootBranch = [{ ...selectedNode }]
          console.log('🎯 BaseTree (RAIZ selecionada):', rootBranch)
          return rootBranch
        } else {
          // Only the selected node, without children (the UI requires showing only it)
          const leafOnly = [{ ...selectedNode, children: [] }]
          console.log('🎯 BaseTree (FILHO selecionado):', leafOnly)
          return leafOnly
        }
      }
      // Fallback: if the node was not found, return the full tree
      return categoryTree.value
    }

    // No category selected, show the full tree
    return categoryTree.value
  })()

  const selectedId = hasSelectedCategory ? String(selectedCategory.value) : null

  // Special rules: no chips and a ROOT with direct products -> hide children in the tree
  let hideChildrenForSelected = false
  if (hasSelectedCategory && !hasChips) {
    const node = findCategoryById(categoryTree.value, selectedCategory.value)
    if (node && !node.parent_id) {
      const directCount = node.count || 0
      hideChildrenForSelected = directCount > 0
    }
  }

  // Recursive function to add counts and filter/expand
  const addCounts = (categories, parentIsSelected = false) => {
    return categories
      .map(cat => {
        const count = categoriesMap.get(String(cat.id)) || 0
        const isSelected = selectedId && String(cat.id) === selectedId
        const children = cat.children ? addCounts(cat.children, isSelected) : []

        let visibleChildren = children.filter(child => child.productCount > 0 || child.children.length > 0)
        if (isSelected && hideChildrenForSelected) {
          visibleChildren = []
        }

        if (isSelected && cat.children && cat.children.length > 0) {
          console.log('🌳 Categoria selecionada:', cat.name, 'ID:', cat.id)
          console.log('  - Total de filhos originais:', cat.children.length)
          console.log('  - Filhos processados:', children.length)
          console.log('  - Filhos visíveis:', visibleChildren.length)
          console.log('  - Filhos com contagem:', children.map(c => ({ name: c.name, count: c.productCount })))
        }

        return {
          ...cat,
          productCount: count,
          children: visibleChildren,
          // Expands automatically if there are visible subcategories OR if it is selected
          expanded: visibleChildren.length > 0 || isSelected
        }
      })
      // In dynamic mode, show categories with products > 0 (or with visible children)
      // and ALWAYS keep the selected category visible
      .filter(cat => {
        if (selectedId && String(cat.id) === selectedId) return true
        if (parentIsSelected) return true // Keeps all children if the parent is selected
        return cat.productCount > 0 || cat.children.length > 0
      })
  }

  const result = addCounts(baseTree)
  // Fallback defensivo
  return result.length > 0 ? result : baseTree
})

// Computed to merge filters with the dynamic search counts
const rootFiltersWithCounts = computed(() => {
  // Enable dynamic mode if there are chips OR a selected category OR selected filters.
  // The stock filter alone does not count: the API returns nothing without a search,
  // category or filter, which would hide every filter. Combined with one of those,
  // the API already applies it to the counts.
  const hasChips = searchChips.value.length > 0
  const hasSelectedCategory = !!selectedCategory.value
  const hasSelectedFilters = Array.isArray(selectedFilters.value) && selectedFilters.value.length > 0
  const inDynamicMode = hasChips || hasSelectedCategory || hasSelectedFilters

  if (!inDynamicMode) {
    return rootFilters.value
  }

  const filtersMap = new Map()

  // Builds a map of all filters (root and children) with their counts
  const mapFilters = (filters) => {
    filters.forEach(filter => {
      filtersMap.set(String(filter.id), filter.productCount || 0)
      if (filter.children && filter.children.length > 0) {
        mapFilters(filter.children)
      }
    })
  }

  mapFilters(rootFiltersWithProducts.value)

  // Recursive function to add counts to the filters
  const addFilterCounts = (filters) => {
    return filters.map(filter => {
      const count = filtersMap.get(String(filter.id)) || 0
      const updatedFilter = {
        ...filter,
        productCount: count,
        children: filter.children ? addFilterCounts(filter.children) : []
      }

      // Filters children with products > 0
      if (updatedFilter.children) {
        updatedFilter.children = updatedFilter.children.filter(child => child.productCount > 0)
      }

      return updatedFilter
    }).filter(filter => filter.productCount > 0) // Keeps only filters with products
  }

  return addFilterCounts(rootFilters.value)
})


// Cache of the last valid result to avoid flickering while loading
const lastCategoryTreeWithCounts = ref([])
const lastRootFiltersWithCounts = ref([])

watch(categoryTreeWithCounts, (val) => {
  if (!dynamicSearchLoading.value) {
    lastCategoryTreeWithCounts.value = val || []
  }
}, { immediate: true, deep: true })

// Keeps the TREE categories based only on the CHIPS
// Reacts to changes in the chips, selected category and stock filter (onlyInStock)
watch([searchChips, selectedCategory, onlyInStock], async () => {
  try {
    const chips = (searchChips.value || []).map(x => (x || '').trim()).filter(Boolean)
    const hasChips = chips.length > 0
    const hasCategory = !!selectedCategory.value
    const hasStockFilter = onlyInStock.value === true

    // Updates chipsOnlyCategories if there are chips OR a category OR a stock filter
    if (hasChips || hasCategory || hasStockFilter) {
      const dynamicSearchText = chips.join(' ')
      const baseOnly = await searchService.dynamicSearch({
        search: dynamicSearchText,
        categoryId: null,
        filters: [],
        inStockOnly: onlyInStock.value
      })
      chipsOnlyCategories.value = baseOnly.categories || []
    } else {
      chipsOnlyCategories.value = []
    }
  } catch (e) {
    console.warn('Falha ao atualizar chips-only categories no watcher', e)
    chipsOnlyCategories.value = []
  }
})

watch(rootFiltersWithCounts, (val) => {
  if (!dynamicSearchLoading.value) {
    lastRootFiltersWithCounts.value = val || []
  }
}, { immediate: true, deep: true })

const displayedCategoryTree = computed(() => {
  if (dynamicSearchLoading.value) {
    if (lastCategoryTreeWithCounts.value && lastCategoryTreeWithCounts.value.length) {
      return lastCategoryTreeWithCounts.value
    }
    // Safe fallback during the first dynamic loading
    return categoryTreeWithCounts.value || categoryTree.value
  }
  return categoryTreeWithCounts.value
})

const displayedRootFiltersWithCounts = computed(() => {
  if (dynamicSearchLoading.value) {
    if (lastRootFiltersWithCounts.value && lastRootFiltersWithCounts.value.length) {
      return lastRootFiltersWithCounts.value
    }
    // Safe fallback during the first dynamic loading
    return rootFiltersWithCounts.value && rootFiltersWithCounts.value.length ? rootFiltersWithCounts.value : rootFilters.value
  }
  return rootFiltersWithCounts.value
})


// Display of the searched term for the "0 results" message
const displayedSearchText = computed(() => {
  const parts = (searchChips.value || []).filter(Boolean)
  const raw = (searchQuerySidebar.value || '').trim()
  const base = parts.length ? parts.join(', ') : raw
  return base || '-'
})

// Suggested term: uses the analytics term when available, otherwise the original term
const suggestedTermDisplay = computed(() => {
  if (suggestedTermFromAnalytics.value) {
    return suggestedTermFromAnalytics.value
  }
  const term = (searchChips.value && searchChips.value[0]) || (searchQuerySidebar.value || '').split(/\s+/)[0] || '-'
  return `[${term}]`
})

// Builds the search parameter from the chips (AND between terms)
const buildSearchParam = () => {
  const chips = (searchChips.value || []).map(x => (x || '').trim()).filter(Boolean)
  if (chips.length > 1) return chips.join(' && ')
  if (chips.length === 1) return chips[0]
  const raw = (searchQuerySidebar.value || '').trim()
  return raw || undefined
}

const suggestedProducts = ref([])
const loadingSuggestions = ref(false)
const suggestedTermFromAnalytics = ref(null) // Termo sugerido do analytics

const fetchSuggestions = async () => {
  try {
    suggestedProducts.value = []
    suggestedTermFromAnalytics.value = null

    // Takes the original search term
    const originalSearchTerm = (searchChips.value && searchChips.value[0]) || (searchQuerySidebar.value || '').trim()

    if (!originalSearchTerm) return

    loadingSuggestions.value = true

    console.log('🔍 Buscando sugestões do analytics para:', originalSearchTerm)

    // Fetches term suggestions from analytics
    const analyticsResponse = await searchAnalyticsService.getSuggestions(originalSearchTerm, 3)

    console.log('📊 Resposta do analytics:', analyticsResponse)

    // If analytics suggestions were found, use the first term
    if (analyticsResponse.suggestions && analyticsResponse.suggestions.length > 0) {
      const suggestedTerm = analyticsResponse.suggestions[0].term
      suggestedTermFromAnalytics.value = suggestedTerm

      console.log('✅ Termo sugerido do analytics:', suggestedTerm)

      // Fetches products using the suggested term
      const resp = await productService.getProducts({
        search: suggestedTerm,
        limit: 8,
        sortBy: 'featured'
      })

      const items = (resp?.items || []).map(product => ({
        ...product,
        description: product[`description_${locale.value}`] || product.description_en || ''
      }))

      suggestedProducts.value = items

      console.log(`📦 Encontrados ${items.length} produtos para o termo sugerido`)
    } else {
      console.log('⚠️  Nenhuma sugestão do analytics encontrada')
    }
  } catch (e) {
    console.error('[CategoryPage] Erro ao buscar sugestões:', e)
    suggestedProducts.value = []
    suggestedTermFromAnalytics.value = null
  } finally {
    loadingSuggestions.value = false
  }
}


const goToSearch = () => {
  addChipsFromInput()
  // Updates dynamic filters with the current chips (dynamic uses a space between terms)
  const chips = (searchChips.value || []).map(x => (x || '').trim()).filter(Boolean)
  const dynamicSearchText = chips.join(' ')
  updateDynamicSearch(dynamicSearchText, selectedCategory.value ?? null)
  // Runs the dynamic search immediately to refresh counts without waiting for the debounce
  runDynamicSearch(selectedCategory.value ?? null)

  // Triggers the search with the search parameter
  currentPage.value = 1
  fetchFilteredProducts()
}
const isApplyingFilters = ref(false)


const filters = ref([])
// selectedFilters declared above to avoid TDZ
const showMobileFilters = ref(false)
const isMobileFiltersExpanded = ref(false)
const isCategoryExpanded = ref(true)
const isPriceExpanded = ref(true)
const priceRange = ref([0, 100000])
const maxPrice = ref(100000)
const totalItems = ref(0)
const itemsPerPage = ref(12)
const itemsPerPageOptions = [12, 24, 48]
const setItemsPerPage = async (n) => {
  if (itemsPerPage.value === n) return
  currentPage.value = 1
  itemsPerPage.value = n
  await fetchFilteredProducts()
}

const sortBy = ref('featured')
const isDragging = ref(false)
const tempPriceRange = ref([0, 1000])
const cartStore = useCartStore()
const togglesStore = useFinancialTogglesStore()
const showToast = ref(false)
const currencySymbol = ref('$')
const showPrices = ref(true) // Controls the price visibility
/** Hides only the price filter in the sidebar / mobile; prices on the cards still follow `showPrices`. */
const showPriceFilter = ref(false)

// Watch to update descriptions when the language changes
watch(locale, (newLocale) => {
  if (products.value.length) {
    products.value = products.value.map(product => ({
      ...product,
      description: product[`description_${newLocale}`] || product.description_en || ''
    }))
  }
})

// Watch to update the sortBy value when the master toggle state changes
watch(showPrices, (newValue) => {
  // If the master toggle is disabled and the current sorting is by price,
  // switch to the default sorting (featured)
  if (!newValue && (sortBy.value === 'priceLowHigh' || sortBy.value === 'priceHighLow')) {
    sortBy.value = 'featured'
    fetchFilteredProducts()
  }
})

// Function to load the financial settings
const loadFinancialSettings = async () => {
  try {
    const settings = await settingsService.getFinancialSettings()

    console.log('🔧 Financial Settings loaded:', settings)
    currencySymbol.value = settings.currency_symbol
    console.log('💱 Currency Symbol set to:', currencySymbol.value)

    // Loads the toggle states
    togglesStore.loadTogglesFromBackend({
      currency_code_enabled: settings.currency_code_enabled,
      currency_symbol_enabled: settings.currency_symbol_enabled,
      tax_rate_enabled: settings.tax_rate_enabled,
      discount_percentage_enabled: settings.discount_percentage_enabled,
      min_order_value_enabled: settings.min_order_value_enabled,
      free_shipping_threshold_enabled: settings.free_shipping_threshold_enabled,
      shipping_cost_enabled: settings.shipping_cost_enabled,
      master_toggle_enabled: settings.master_toggle_enabled
    })

    // Updates the price visibility based on the master toggle
    showPrices.value = togglesStore.masterToggle
    console.log('Master toggle state:', togglesStore.masterToggle)
    console.log('Show prices:', showPrices.value)
  } catch (error) {
    console.error('Error loading financial settings:', error)
  }
}

const fetchFilters = async () => {
  try {
    console.log('🔖 Buscando filtros hierárquicos...')
    // Passes categoryId to optimize the count query
    const categoryId = selectedCategory.value
    const hierarchicalFilters = await filterService.getHierarchical(categoryId)
    filters.value = hierarchicalFilters
    console.log('✅ Filtros carregados:', hierarchicalFilters)
  } catch (err) {
    console.error('❌ Erro ao buscar filtros:', err)
    filters.value = []
  }
}

const fetchFilteredProducts = async () => {
  try {
    // Keeps the current list on screen while the new one loads (anti-flicker)
    lastProducts.value = [...products.value]
    loading.value = true

    // Prepares the filter parameters
    const filterParams = {
      page: currentPage.value,
      limit: itemsPerPage.value,
      sortBy: sortBy.value,
      search: buildSearchParam(),
      inStockOnly: onlyInStock.value,
      locationId: selectedLocation.value?.id || null  // Adds locationId for dynamic price calculation
    }

    // ============================================================================
    // COMBINED FILTERING LOGIC (always combines with the category when there is one)
    // ============================================================================
    const hasFilters = Array.isArray(selectedFilters.value) && selectedFilters.value.length > 0
    const hasCategory = !!selectedCategory.value

    if (hasCategory) {
      // Checks whether we are in dynamic mode (with chips)
      const hasChips = searchChips.value.length > 0

      console.log('🔍 Buscando categoria:', selectedCategory.value)
      console.log('🔍 Modo dinâmico (com chips):', hasChips)
      console.log('🔍 categoryTreeWithCounts:', categoryTreeWithCounts.value)
      console.log('🔍 categoryTree:', categoryTree.value)

      // Finds the selected category in the appropriate tree
      const treeToSearch = hasChips ? categoryTreeWithCounts.value : categoryTree.value
      const category = findCategoryById(treeToSearch, selectedCategory.value)

      console.log('🔍 Categoria encontrada:', category)

      if (category) {
        // In dynamic mode (with chips), uses productCount; otherwise, uses count
        const directProductCount = hasChips ? (category.productCount || 0) : (category.count || 0)
        const hasDirectProducts = directProductCount > 0

        console.log('🔍 Contagem de produtos diretos:', directProductCount)
        console.log('🔍 Tem produtos diretos?', hasDirectProducts)
        console.log('🔍 Filhos da categoria:', category.children)

        if (hasDirectProducts) {
          // If it has direct products, search only in this category
          filterParams.categoryId = selectedCategory.value
          console.log('✅ Restringindo por CATEGORIA (com produtos diretos):', selectedCategory.value, 'count:', directProductCount)
        } else {
          // If it has no direct products, search in all children recursively
          const allChildrenIds = getAllChildrenIds(category)
          filterParams.categoryIds = allChildrenIds
          console.log('✅ Restringindo por CATEGORIA PAI (sem produtos diretos) + FILHOS:', allChildrenIds)
          console.log('✅ Categoria completa:', category)
          console.log('✅ Filhos da categoria:', category.children)
        }
      } else {
        // Fallback: if the category is not found in the tree, use only the ID
        filterParams.categoryId = selectedCategory.value
        console.log('⚠️ Restringindo por CATEGORIA (fallback - não encontrada na árvore):', selectedCategory.value)
      }
    }

    if (hasFilters) {
      filterParams.filters = selectedFilters.value
      console.log('🔍 Aplicando FILTROS DINÂMICOS:', selectedFilters.value)
    }

    // Note: with no category, filters or search, the backend returns everything by default

    // Price range only when the price filter is visible (avoids unnecessary /products?minPrice&maxPrice)
    if (showPrices.value && showPriceFilter.value) {
      filterParams.minPrice = priceRange.value[0]
      filterParams.maxPrice = priceRange.value[1]
    }

    console.log('Buscando produtos com os seguintes filtros:', filterParams)

    // Sets pricesLoading to true before fetching products
    pricesLoading.value = true

    const [productsResponse, settings] = await Promise.all([
      productService.getProducts(filterParams),
      settingsService.getFinancialSettings()
    ])

    console.log('Resposta da API de produtos:', productsResponse)

    // Maps products and displays them immediately
    let mapped = productsResponse.items.map(product => ({
      ...product,
      description: product[`description_${locale.value}`] || product.description_en || ''
    }))
    if (showPrices.value && showPriceFilter.value) {
      mapped = filterProductsByDisplayedPriceRange(
        mapped,
        priceRange.value[0],
        priceRange.value[1],
      )
    }
    products.value = mapped

    // Products are already displayed, turn off the main loading
    loading.value = false

    totalItems.value = productsResponse.total
    totalPages.value = Math.ceil(productsResponse.total / itemsPerPage.value)
    currencySymbol.value = settings.currency_symbol

    // Simulates a small delay for the price loading (visual effect)
    // In practice, prices already come from the API, but this creates the impression of async loading
    setTimeout(() => {
      pricesLoading.value = false
    }, 300)

    // Fetch suggestions when there are no results
    if ((productsResponse.total || 0) === 0) {
      await fetchSuggestions()
    } else {
      suggestedProducts.value = []
    }

    // Automatic redirect: if only 1 product is left after applying filters, redirect to the details page
    if (productsResponse.total === 1 && products.value.length === 1) {
      const singleProduct = products.value[0]
      const productDescription = singleProduct[`description_${locale.value}`] || 
                                  singleProduct.description_en || 
                                  singleProduct.description_fr || 
                                  singleProduct.name || ''
      
      const productUrl = getProductUrlWithDescription(singleProduct.id, productDescription)
      
      console.log('🔄 Apenas 1 produto encontrado após filtros, redirecionando para:', productUrl)
      
      // Redirects to the product details page
      router.push(productUrl)
      return // Stops execution to avoid rendering the list
    }

  } catch (err) {
    console.error('Error fetching data:', err)
    error.value = 'Error loading products'
    products.value = []
    loading.value = false
    pricesLoading.value = false
  }
}

const handleSort = () => {
  currentPage.value = 1 // Reset to the first page when the sorting changes
  fetchFilteredProducts()
}

const handlePriceRangeChange = () => {

  currentPage.value = 1 // Reset to the first page when the price range changes
  fetchFilteredProducts()
}

// Updates the minimum value of the price range
const updateMinPrice = (event) => {
  const minValue = parseInt(event.target.value)
  const maxValue = priceRange.value[1]

  // Ensures the minimum value does not exceed the maximum value
  if (minValue < maxValue) {
    priceRange.value = [minValue, maxValue]
  } else {
    // If the minimum value is greater than the maximum, set it to the maximum - 1
    priceRange.value = [maxValue - 1, maxValue]
    event.target.value = maxValue - 1
  }
}

// Updates the maximum value of the price range
const updateMaxPriceSlider = (event) => {
  const maxValue = parseInt(event.target.value)
  const minValue = priceRange.value[0]

  // Ensures the maximum value is not lower than the minimum value
  if (maxValue > minValue) {
    priceRange.value = [minValue, maxValue]
  } else {
    // If the maximum value is lower than the minimum, set it to the minimum + 1
    priceRange.value = [minValue, minValue + 1]
    event.target.value = minValue + 1
  }
}

// Cache of the previous list for anti-flicker
const lastProducts = ref([])
// Computed property that shows the previous list while loading
const filteredProducts = computed(() => {
  if (loading.value && lastProducts.value && lastProducts.value.length) {
    return lastProducts.value
  }
  return products.value
})

// Computed for the available sorting options
const sortOptions = computed(() => {
  const options = [
    { value: 'featured', label: 'categoryPage.sortBy.featured' },
    { value: 'nameAZ', label: 'categoryPage.sortBy.nameAZ' },
    { value: 'nameZA', label: 'categoryPage.sortBy.nameZA' }
  ]

  // Adds price sorting options only if the master toggle is enabled
  if (showPrices.value) {
    options.splice(1, 0,
      { value: 'priceLowHigh', label: 'categoryPage.sortBy.priceLowHigh' },
      { value: 'priceHighLow', label: 'categoryPage.sortBy.priceHighLow' }
    )
  }

  return options
})

// Computed for the pages to display
const displayedPages = computed(() => {
  const pages = []
  const maxVisiblePages = 5
  let start = Math.max(1, currentPage.value - Math.floor(maxVisiblePages / 2))
  let end = Math.min(totalPages.value, start + maxVisiblePages - 1)

  if (end - start + 1 < maxVisiblePages) {
    start = Math.max(1, end - maxVisiblePages + 1)
  }

  for (let i = start; i <= end; i++) {
    pages.push(i)
  }
  return pages
})

// Computed property to calculate the item range
const itemRange = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage.value + 1
  const end = Math.min(start + itemsPerPage.value - 1, totalItems.value)
  return { start, end }
})

// Watcher to react to filter changes and sync the dynamic search
watch([selectedCategory, selectedFilters], async () => {
  console.log('Filtros alterados:', {
    selectedCategory: selectedCategory.value,
    selectedFilters: selectedFilters.value
  });

  isApplyingFilters.value = true
  try {
    // 1) Updates the query (without triggering another manual run)
    const chips = (searchChips.value || []).map(x => (x || '').trim()).filter(Boolean)
    const dynamicSearchText = chips.join(' ')
    updateDynamicSearch(dynamicSearchText, selectedCategory.value ?? null)

    // 2) Runs the dynamic search and waits for it to finish (avoids flickering)
    await setDynamicFilters(selectedFilters.value)

    // 3) Only then updates the product listing
    currentPage.value = 1

    await updateMaxPrice()

    console.log('Buscando produtos após mudança nos filtros')
    await fetchFilteredProducts()
  } finally {
    isApplyingFilters.value = false
  }
}, { deep: true })

const fetchCategories = async () => {
  try {
    loadingCategories.value = true
    // Fetch categories directly from the backend with a hierarchical structure
    // and only active categories with products
    const hierarchicalCategories = await categoryService.searchCategories('', true)

    // Ensure all categories are collapsed by default
    const setAllCollapsed = (tree) => {
      tree.forEach(category => {
        // Marks the category as collapsed
        category.expanded = false

        // Recursively processes the children
        if (category.children && category.children.length > 0) {
          setAllCollapsed(category.children)
        }
      })
    }

    // Apply the collapse to all categories
    setAllCollapsed(hierarchicalCategories)

    // Use the hierarchical categories with everything collapsed
    categoryTree.value = hierarchicalCategories

    // List categories for debugging
    const listCategoriesWithPath = (tree, path = '') => {
      tree.forEach(category => {
        const currentPath = path ? `${path} > ${category.name}` : category.name
        console.log(`[CategoryPage] Categoria carregada: ${currentPath} (ID: ${category.id}, expanded: ${category.expanded})`)

        if (category.children && category.children.length > 0) {
          listCategoriesWithPath(category.children, currentPath)
        }
      })
    }

    listCategoriesWithPath(categoryTree.value)

    // Removed unnecessary log about root categories
  } catch (err) {
    console.error('Error fetching categories:', err)
    error.value = 'Error loading categories'
    categoryTree.value = []
  } finally {
    loadingCategories.value = false
  }
}



// Function to fetch hierarchical filters
const fetchRootFilters = async () => {
  try {
    // Passes categoryId to optimize the count query
    const categoryId = selectedCategory.value
    const hierarchicalFilters = await filterService.getHierarchical(categoryId)
    rootFilters.value = hierarchicalFilters || []
    console.log('Root filters loaded:', rootFilters.value)
  } catch (err) {
    console.error('Error fetching root filters:', err)
    rootFilters.value = []
  }
}

// Same category scoping as fetchFilteredProducts (1 id vs children), so the ceiling matches the list
const resolveCategoryScopeForPrices = () => {
  const catId = selectedCategory.value
  if (!catId) {
    return { categoryId: null, categoryIds: null }
  }
  const hasChips = searchChips.value.length > 0
  const treeToSearch = hasChips ? categoryTreeWithCounts.value : categoryTree.value
  const category = findCategoryById(treeToSearch, catId)
  if (!category) {
    return { categoryId: catId, categoryIds: null }
  }
  const directProductCount = hasChips ? (category.productCount || 0) : (category.count || 0)
  const hasDirectProducts = directProductCount > 0
  if (hasDirectProducts) {
    return { categoryId: catId, categoryIds: null }
  }
  const allChildrenIds = getAllChildrenIds(category)
  if (allChildrenIds.length > 0) {
    return { categoryId: null, categoryIds: allChildrenIds }
  }
  return { categoryId: catId, categoryIds: null }
}

// Quick estimated ceiling (API) aligned with the category scope in the listing
const updateMaxPrice = async () => {
  if (!showPrices.value) {
    console.log('Preços desabilitados, não atualizando o preço máximo');
    return;
  }
  if (!showPriceFilter.value) {
    return;
  }

  /* To re-enable the filter: `showPriceFilter = true` and uncomment this block.
  try {
    const scope = resolveCategoryScopeForPrices()
    const ceil = await productService.getSliderPriceCeil(
      scope.categoryId,
      [],
      selectedLocation.value?.id || null,
      scope.categoryIds,
    );
    maxPrice.value = ceil;
    priceRange.value = [0, ceil];
    console.log(`Preço máximo do slider atualizado para: ${ceil}`);
  } catch (err) {
    console.error('Erro ao buscar preço máximo:', err);
    maxPrice.value = 100000;
    priceRange.value = [0, 100000];
  }
  */
}

const getProductImage = (product) => {
  console.log(`[CategoryPage] Obtendo imagem para produto: ${product.id} - ${product.name}`);
  console.log(`[CategoryPage] Caminho da imagem original: ${product.image}`);
  console.log(`[CategoryPage] FoxPro code: ${product.foxpro_code}`);
  const imageUrl = imageService.getProductImageUrl(product.image, product);
  console.log(`[CategoryPage] Caminho da imagem processado: ${imageUrl}`);
  return imageUrl;
}

const handleImageError = (e) => {
  imageService.handleImageError(e)
}

const selectCategory = async (categoryId) => {
  // Toggle: clicking the same category unselects it
  if (selectedCategory.value === categoryId) {
    // When unselecting the category via "X", also reset the dynamic filters
    selectedFilters.value = []
    selectedCategory.value = null
    currentPage.value = 1
    // Clears the categories-only (chips) used in the TREE
    chipsOnlyCategories.value = []

    // Updates the query for the dynamic search; the watcher will run the search and refresh products
    const chips = (searchChips.value || []).map(x => (x || '').trim()).filter(Boolean)
    const dynamicSearchText = chips.join(' ')
    updateDynamicSearch(dynamicSearchText, null)
    // Updates the URL to the full category list via Vue Router (SPA)
    // Keeps the keyword parameter in the URL when there are chips
    const query = chips.length > 0 ? { keyword: chips.join(' ') } : {}
    isInternalRouteChange.value = true
    router.replace({ path: '/categories', query })
    setTimeout(() => {
      isInternalRouteChange.value = false
    }, 100)
    return
  }

  console.log('Categoria selecionada:', categoryId);

  // Do NOT enable loading here - let the cache (lastProducts) keep the previous list visible
  // until the new data arrives (smooth transition without flickering)

  try {
    // Reset the current page when changing category
    currentPage.value = 1;

    // Sets the newly selected category
    selectedCategory.value = categoryId;

    // Whenever a category is chosen, clear the selected dynamic filters
    selectedFilters.value = [];

    // Keeps the search chips - they must remain when selecting a category
    // The tree counters are always based on the chips

    // Fetches categories-only (based only on the CHIPS) to feed the TREE
    // Without affecting the dynamic FILTERS (which must consider the selected category)
    try {
      const chips = (searchChips.value || []).map(x => (x || '').trim()).filter(Boolean)
      const dynamicSearchText = chips.join(' ')
      const baseOnly = await searchService.dynamicSearch({
        search: dynamicSearchText,
        categoryId: null,
        filters: [],
        inStockOnly: onlyInStock.value
      })
      chipsOnlyCategories.value = baseOnly.categories || []
      console.log('📈 Chips-only categories carregadas para ÁRVORE:', chipsOnlyCategories.value.length)
    } catch (e) {
      console.warn('⚠️ Falha ao obter chips-only categories; usando retorno padrão da busca dinâmica', e)
      chipsOnlyCategories.value = []
    }

    // Updates the maximum price based on the selected category
    await updateMaxPrice();

    // Explicitly fetches filtered products
    await fetchFilteredProducts();

    // Updates the URL without reloading the page (after fetching products to avoid a re-trigger)
    // The 'slug' parameter in the route is actually the category ID
    // Keeps the keyword parameter in the URL when there are chips
    const chips = (searchChips.value || []).map(x => (x || '').trim()).filter(Boolean)
    const query = chips.length > 0 ? { keyword: chips.join(' ') } : {}
    isInternalRouteChange.value = true
    router.replace({ path: `/categories/${categoryId}`, query })
    // Resets the flag after a small delay so the watcher can process it
    setTimeout(() => {
      isInternalRouteChange.value = false
    }, 100)

    console.log('Produtos carregados para a categoria:', selectedCategory.value);

    // Scroll to the sorting area to make sure it is visible
    setTimeout(() => {
      const sortingElement = document.querySelector('.flex.justify-between.items-center.p-3.border-2.border-black')
      if (sortingElement) {
        const yOffset = -20 // Adjustment to make sure the sorting area stays visible
        const y = sortingElement.getBoundingClientRect().top + window.pageYOffset + yOffset
        window.scrollTo({ top: y, behavior: 'smooth' })
      }
    }, 100); // Small delay to make sure the DOM has been updated
  } catch (error) {
    console.error('Erro ao selecionar categoria:', error);
  }
  // Do NOT disable loading here - fetchFilteredProducts() already manages loading internally
}

const handleAddToCart = (product, quantity) => {
  // Checks whether the product has characteristics that need to be selected
  if (productCharacteristicsService.hasCharacteristics(product)) {
    // If it has characteristics, redirect to the product details page
    router.push({
      path: `/product/${product.id}`,
      query: { showValidation: 'true' } // Passes a parameter to show the validation
    });
    return;
  }

  // Check whether the product has multiple units of measurement
  const hasMultipleUnits = product.unit_of_measure &&
    product.unit_of_measure.startsWith('{') &&
    product.unit_of_measure.endsWith('}')

  if (hasMultipleUnits) {
    // If it has multiple units, redirect to the product page
    router.push(`/product/${product.id}`)
    return
  }

  // If it has no characteristics and no multiple units, add it straight to the cart
  // Get the default unit of the product
  const defaultUnit = getDefaultUnit(product.unit_of_measure)

  const cartItem = {
    id: product.id,
    name: product.name,
    price: product.price,
    quantity: quantity,
    image: getProductImage(product),
    foxpro_code: product.foxpro_code,
    unit: defaultUnit // Incluir a unidade de medida
  }

  cartStore.addItem(cartItem)
  showSuccessToast()
}

const showSuccessToast = () => {
  showToast.value = true
  setTimeout(() => {
    showToast.value = false
  }, 3000)
}

const handlePageChange = async (page) => {
  // Do NOT enable loading here - let the cache (lastProducts) keep the previous list visible
  // until the new data arrives (smooth transition without flickering)

  try {
    currentPage.value = page
    await fetchFilteredProducts()

    // Scroll to the sorting area to make sure it is visible
    setTimeout(() => {
      const sortingElement = document.querySelector('.flex.justify-between.items-center.p-3.border-2.border-black')
      if (sortingElement) {
        const yOffset = -20 // Adjustment to make sure the sorting area stays visible
        const y = sortingElement.getBoundingClientRect().top + window.pageYOffset + yOffset
        window.scrollTo({ top: y, behavior: 'smooth' })
      } else {
        // Fallback to the previous behaviour
        window.scrollTo({ top: 0, behavior: 'smooth' })
      }
    }, 100) // Small delay to make sure the DOM has been updated
  } catch (error) {
    console.error('Erro ao mudar de página:', error)
  }
  // Do NOT disable loading here - fetchFilteredProducts() already manages loading internally
}

// Function to select the first category
const selectFirstCategory = () => {
  console.log('[CategoryPage] Tentando selecionar a primeira categoria')

  // Checks whether there are categories available
  if (categoryTree.value && categoryTree.value.length > 0) {
    console.log(`[CategoryPage] Selecionando primeira categoria: ${categoryTree.value[0].name} (ID: ${categoryTree.value[0].id})`)
    selectCategory(categoryTree.value[0].id)

    // Scrolls to the selected category
    scrollToCategoryItem(categoryTree.value[0].id)
  } else {
    console.log('[CategoryPage] Nenhuma categoria disponível para seleção')
    // Tries to fetch the categories again
    fetchCategories().then(() => {
      if (categoryTree.value && categoryTree.value.length > 0) {
        selectCategory(categoryTree.value[0].id)

        // Scrolls to the selected category
        scrollToCategoryItem(categoryTree.value[0].id)
      }
    })
  }
}

// Function to select a category by slug
// Function to scroll to the selected item in the category filter
const scrollToCategoryItem = (categoryId) => {
  console.log(`[CategoryPage] Rolando até a categoria selecionada: ${categoryId}`)

  // We increased the delay to make sure the DOM has been fully updated
  setTimeout(() => {
    try {
      // Fetches all elements that may contain the category ID
      const categoryElements = document.querySelectorAll(`[data-category-id="${categoryId}"]`)
      console.log(`[CategoryPage] Encontrados ${categoryElements.length} elementos com data-category-id=${categoryId}`)

      // Finds the most specific element (the category header)
      let selectedElement = null
      categoryElements.forEach(el => {
        if (el.classList.contains('category-header') || el.querySelector('.category-header')) {
          selectedElement = el
          console.log('[CategoryPage] Encontrado elemento header da categoria')
        }
      })

      // If the header was not found, use the first element found
      if (!selectedElement && categoryElements.length > 0) {
        selectedElement = categoryElements[0]
        console.log('[CategoryPage] Usando o primeiro elemento encontrado')
      }

      if (selectedElement) {
        console.log(`[CategoryPage] Elemento da categoria encontrado, rolando até ele`)

        // Finds the category filter container (desktop and mobile)
        const desktopContainer = document.querySelector('.category-filter-container')
        const mobileContainer = document.querySelector('.category-filter-container-mobile')

        // Determines which container is visible
        const container = window.innerWidth >= 1024 ? desktopContainer : mobileContainer

        if (container) {
          console.log(`[CategoryPage] Container encontrado: ${container.className}`)

          // Forces the container to be scrollable
          container.style.overflowY = 'auto'

          // Uses scrollIntoView to scroll to the element
          selectedElement.scrollIntoView({ behavior: 'smooth', block: 'center' })

          console.log(`[CategoryPage] Rolagem iniciada para o elemento`)
        } else {
          console.log(`[CategoryPage] Container de filtro de categorias não encontrado`)
        }
      } else {
        console.log(`[CategoryPage] Elemento da categoria com ID ${categoryId} não encontrado no DOM`)
      }
    } catch (error) {
      console.error(`[CategoryPage] Erro ao tentar rolar até a categoria:`, error)
    }
  }, 500) // We increased the delay to 500ms to make sure the DOM has been fully updated
}

// Helper function to find a category by ID in the tree
const findCategoryById = (tree, id) => {
  for (const node of tree) {
    if (node.id === id) {
      return node
    }
    if (node.children && node.children.length > 0) {
      const found = findCategoryById(node.children, id)
      if (found) return found
    }
  }
  return null
}

// Function to collect all child IDs recursively
const getAllChildrenIds = (category) => {
  const ids = [category.id]

  if (category.children && category.children.length > 0) {
    category.children.forEach(child => {
      ids.push(...getAllChildrenIds(child))
    })
  }

  return ids
}

const selectCategoryBySlug = async (slug) => {
  console.log(`[CategoryPage] Tentando selecionar categoria por slug: ${slug}`)

  // Waits for the categories to load if they are not loaded yet
  if (loadingCategories.value) {
    console.log(`[CategoryPage] Aguardando carregamento das categorias...`)
    await new Promise(resolve => {
      const checkLoading = setInterval(() => {
        if (!loadingCategories.value) {
          clearInterval(checkLoading)
          resolve()
        }
      }, 100)
    })
  }

  // Checks whether we have categories loaded
  if (categoryTree.value && categoryTree.value.length > 0) {
    console.log(`[CategoryPage] Buscando categoria com ID ${slug} na árvore de categorias`)

    // Function to find the path to the category
    const findPathToCategory = (tree, id, path = []) => {
      for (const node of tree) {
        // Tries with the current node
        const currentPath = [...path, node]
        if (node.id === id) {
          return currentPath
        }
        // Tries with the children
        if (node.children && node.children.length > 0) {
          const foundPath = findPathToCategory(node.children, id, currentPath)
          if (foundPath) return foundPath
        }
      }
      return null
    }

    // Finds the path to the category
    const categoryPath = findPathToCategory(categoryTree.value, slug)

    if (categoryPath) {
      console.log(`[CategoryPage] Categoria encontrada na árvore: ${categoryPath[categoryPath.length-1].name} (ID: ${slug})`)
      console.log(`[CategoryPage] Caminho até a categoria: ${categoryPath.map(c => c.name).join(' > ')}`)

      // Expands all categories along the path
      categoryPath.forEach(cat => {
        cat.expanded = true
      })

      // Updates the category tree to reflect the expansions
      categoryTree.value = [...categoryTree.value]

      // Selects the category
      await selectCategory(slug)

      // Scrolls to the selected item
      scrollToCategoryItem(slug)

      return
    } else {
      console.log(`[CategoryPage] Categoria com ID ${slug} não encontrada na árvore de categorias`)
    }
  }

  // If we got this far, try fetching the category directly from the backend
  try {
    console.log(`[CategoryPage] Tentando buscar a categoria ${slug} diretamente do backend`)

    // Fetch categories with the hierarchical structure including the given category
    await fetchCategories()

    // Try again to find the category in the updated tree
    return selectCategoryBySlug(slug)
  } catch (error) {
    console.error(`[CategoryPage] Erro ao buscar categoria ${slug} do backend:`, error)
  }

  // If we got this far, do not select any category
  console.log(`[CategoryPage] Categoria ${slug} não encontrada, não selecionando nenhuma categoria`)
  selectedCategory.value = null
}

onMounted(async () => {
  try {
    console.log('🚀 Inicializando a página de categorias')
    console.log('📍 Rota atual:', route.path)
    console.log('📍 Parâmetros da rota:', route.params)
    console.log('📍 Query da rota:', route.query)

    // Loads the selected location from sessionStorage
    selectedLocation.value = locationService.getSelectedLocation()
    console.log('Localização selecionada:', selectedLocation.value)

    // Listener for location changes
    eventBus.on('selected-location-changed', async (newLocation) => {
      console.log('🔄 Localização alterada, atualizando preços:', newLocation)
      selectedLocation.value = newLocation
      // Reloads products with the new prices
      await fetchFilteredProducts()
    })

    await loadFinancialSettings()
    await fetchCategories()
    
    // Loads filters in parallel for better performance
    console.log('🔄 Carregando filtros em paralelo...')
    await Promise.all([
      fetchFilters(),
      fetchRootFilters()
    ])
    console.log('✅ Filtros carregados')

    // Checks whether there is a keyword parameter in the query
    const hasKeyword = !!route.query.keyword
    if (hasKeyword) {
      console.log('Keyword encontrada na query:', route.query.keyword)
      // Adds the keyword to the search chips
      const keywords = route.query.keyword.split(' ').filter(k => k.trim())
      searchChips.value = keywords
      searchQuerySidebar.value = ''

      // Updates dynamic filters with the keyword
      const dynamicSearchText = keywords.join(' ')
      updateDynamicSearch(dynamicSearchText, null)
      await runDynamicSearch(null)
    }

    // Checks whether the route has a category ID
    if (route.params.slug) {
      console.log('ID de categoria encontrado na rota:', route.params.slug)
      // Selects the category given in the route and expands its parents
      await selectCategoryBySlug(route.params.slug)
    } else {
      // If there is no ID in the route, do not select any category
      // Show random products without a filter
      console.log('Nenhum ID de categoria na rota, exibindo produtos sem filtro')
      selectedCategory.value = null
    }

    console.log('Categoria selecionada após inicialização:', selectedCategory.value)
    await updateMaxPrice()
    await fetchFilteredProducts()
  } catch (err) {
    console.error('Error in initialization:', err)
    error.value = 'Error loading page'
  }
})

// Watch for changes in the keyword query parameter
watch(
  () => route.query.keyword,
  async (newKeyword, oldKeyword) => {
    // Ignores route changes caused internally (avoids a loop)
    if (isInternalRouteChange.value) {
      console.log('Mudança de keyword interna detectada, ignorando watcher')
      return
    }

    // Avoids processing if it is the same keyword
    if (newKeyword === oldKeyword) {
      return
    }

    if (newKeyword) {
      console.log('Keyword na query alterada para:', newKeyword)
      // Adds the keyword to the search chips
      const keywords = newKeyword.split(' ').filter(k => k.trim())
      searchChips.value = keywords
      searchQuerySidebar.value = ''

      // Clears the selected category when there is a keyword
      selectedCategory.value = null

      // Updates dynamic filters with the keyword
      const dynamicSearchText = keywords.join(' ')
      updateDynamicSearch(dynamicSearchText, null)
      await runDynamicSearch(null)

      currentPage.value = 1
      await updateMaxPrice()
      await fetchFilteredProducts()
    } else {
      // If the keyword was removed, clear the chips
      console.log('Keyword removida da query')
      searchChips.value = []
      searchQuerySidebar.value = ''
      await updateMaxPrice()
    }
  }
)

// Watch for route changes to update the selected category
watch(
  () => route.params.slug,
  async (newSlug) => {
    console.log('ID de categoria na rota alterado para:', newSlug)

    // Ignores route changes caused internally (avoids a loop)
    if (isInternalRouteChange.value) {
      console.log('Mudança de rota interna detectada, ignorando watcher')
      return
    }

    // Ensures the categories are loaded
    if (loadingCategories.value) {
      await new Promise(resolve => {
        const checkLoading = setInterval(() => {
          if (!loadingCategories.value) {
            clearInterval(checkLoading)
            resolve()
          }
        }, 100)
      })
    }

    // Resets all categories to collapsed
    const resetAllExpanded = (tree) => {
      tree.forEach(category => {
        category.expanded = false
        if (category.children && category.children.length > 0) {
          resetAllExpanded(category.children)
        }
      })
    }
    resetAllExpanded(categoryTree.value)

    // Updates the tree to reflect the changes
    categoryTree.value = [...categoryTree.value]

    if (newSlug) {
      // If there is an ID in the route, select the category and expand its parents
      await selectCategoryBySlug(newSlug)
      await updateMaxPrice()
      await fetchFilteredProducts()
    } else {
      console.log('ID de categoria removido da rota, limpando seleção')
      if (selectedCategory.value !== null) {
        selectedCategory.value = null
        await updateMaxPrice()
        await fetchFilteredProducts()
      }
      // Do not call selectFirstCategory() here to avoid a loop
      // Selecting the first category must only happen in onMounted
    }
  }
)

// Debounced function that is only called when the user stops dragging
const debouncedFetchProducts = debounce(() => {
  console.log('🎯 Valores atuais:', {
    isDragging: isDragging.value,
    tempRange: tempPriceRange.value,
    currentRange: priceRange.value
  })

  // If NOT dragging AND the values are different, make the request
  if (!isDragging.value &&
      (tempPriceRange.value[0] !== priceRange.value[0] ||
       tempPriceRange.value[1] !== priceRange.value[1])) {
    console.log('🔄 Fazendo requisição após debounce')
    currentPage.value = 1
    fetchFilteredProducts()
    tempPriceRange.value = [...priceRange.value]
  } else {
    console.log('❌ Requisição ignorada porque:',
      isDragging.value ? 'ainda está arrastando' : 'valores não mudaram')
  }
}, 220)

const handleRangeStart = () => {
  isDragging.value = true
  console.log('🟢 isDragging:', isDragging.value, '- Início do arrasto')
  tempPriceRange.value = [...priceRange.value]
}

const handleRangeEnd = () => {
  isDragging.value = false
  console.log('🔴 isDragging:', isDragging.value, '- Fim do arrasto')
  if (tempPriceRange.value[0] !== priceRange.value[0] ||
      tempPriceRange.value[1] !== priceRange.value[1]) {
    debouncedFetchProducts()
  }
}

// Watch only for when isDragging changes to false
watch(isDragging, (newValue) => {
  console.log('👀 Watch isDragging:', newValue, {
    tempRange: tempPriceRange.value,
    currentRange: priceRange.value
  })

  if (!newValue &&
      (tempPriceRange.value[0] !== priceRange.value[0] ||
       tempPriceRange.value[1] !== priceRange.value[1])) {
    debouncedFetchProducts()
  }
})

const navigateToProduct = (productId) => {
  router.push(`/product/${productId}`)
}

const formatPrice = (price, product = null) => {
  // If the product has lpor = true, return "Prix sur demande"
  if (product && product.lpor === true) {
    return t('products.priceOnRequest')
  }
  if (price === null || price === undefined) {
    return t('products.priceToConsult')
  }
  const numericPrice = typeof price === 'string' ? parseFloat(price) : Number(price)
  // If the price is NaN or negative, show "À consultar"
  // Price 0.00 is valid and must be displayed normally
  if (isNaN(numericPrice) || numericPrice < 0) {
    return t('products.priceToConsult')
  }
  return `${currencySymbol.value}${numericPrice.toFixed(2)}`
}

</script>

<script>
/* eslint-disable vue/no-dupe-keys */
export default {
  name: 'CategoryPage',
  components: {
    ProductQuantitySelector,
    PriceSkeleton
  },
  data() {
    return {
      showMobileFilters: false,
      selectedBrands: [],
      priceRange: [0, 100000],
      maxPrice: 100000,
      selectedCategory: null
    }
  },
  methods: {
    handleImageError(e) {
      e.target.src = PLACEHOLDER_IMAGE_PATH
      e.target.onerror = null
    },
    addToCart(product, quantity) {
      console.log('Adding to cart:', product, 'quantity:', quantity)
    },
    selectCategory(categoryId) {
      // If a category is already selected and it is the same one being clicked, do nothing
      if (this.selectedCategory === categoryId) {
        return; // Does not allow unselecting the category
      }

      // Sets the newly selected category
      this.selectedCategory = categoryId;
    }
  }
}
</script>
<style scoped>
input[type="range"]::-webkit-slider-thumb {
  pointer-events: auto;
  -webkit-appearance: none;
  width: 16px;
  height: 16px;
  background: #2C2C2C;
  border-radius: 50%;
  cursor: pointer;
}

input[type="range"]::-moz-range-thumb {
  pointer-events: auto;
  width: 16px;
  height: 16px;
  background: #2C2C2C;
  border-radius: 50%;
  cursor: pointer;
  border: none;
}

/* Custom styling for the checkboxes */
input[type="checkbox"] {
  appearance: none;
  -webkit-appearance: none;
  width: 24px;
  height: 24px;
  border: 2px solid rgba(0, 0, 0, 0.7);
  border-radius: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

input[type="checkbox"]:checked {
  background-color: #FBBD1E;
  border-color: #FBBD1E;
}

input[type="checkbox"]:checked::after {
  content: '';
  width: 12px;
  height: 12px;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='white'%3E%3Cpath d='M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z'/%3E%3C/svg%3E");
  background-size: contain;
  background-repeat: no-repeat;
}

.selected-category {
  background-color: rgba(251, 189, 30, 0.1); /* Yellow color with transparency */
}

.selected-category:hover {
  background-color: rgba(251, 189, 30, 0.2); /* Cor amarela um pouco mais escura no hover */
}

.loader-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.loader-spinner {
  border: 4px solid rgba(251, 189, 30, 0.3);
  border-top: 4px solid #FBBD1E;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin-bottom: 10px;
}

.loader-text {
  font-family: 'Archivo', sans-serif;
  font-size: 14px;
  color: #666;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.aspect-square {
  aspect-ratio: 1 / 1;
  object-fit: cover;
}

@media (max-width: 768px) {
  .aspect-square {
    aspect-ratio: 1 / 0.75; /* Slightly smaller ratio for mobile */
  }
}

/* Removing the default select icon on Chrome/Safari */
select {
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
}

/* Removing the default select icon on IE/Edge */
select::-ms-expand {
  display: none;
}

/* Making sure the select stays on top of the custom icon */
select {
  position: relative;
  z-index: 1;
  background-color: transparent;
}

/* Style for the select hover */
select:hover {
  border-color: #FFDD00;
}

/* Style for the select focus */
select:focus {
  outline: none;
  border-color: #FFDD00;
  box-shadow: 0 0 0 2px rgba(255, 221, 0, 0.2);
}

/* Add these styles to improve the interaction */
.cursor-pointer:hover {
  transform: translateY(-2px);
  transition: transform 0.2s ease-in-out;
  box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1);
}

/* Styles for the price sliders */
input[type="range"] {
  -webkit-appearance: none;
  appearance: none;
  height: 5px;
  background: #E0E0E0; /* Back to the original light gray */
  border-radius: 5px;
  background-image: linear-gradient(#F9C349, #F9C349);
  background-repeat: no-repeat;
}

input[type="range"]::-webkit-slider-thumb {
  -webkit-appearance: none;
  height: 22px;
  width: 22px;
  border-radius: 50%;
  background: #F9C349; /* Changed from white to #F9C349 */
  border: 2px solid #F9C349;
  cursor: pointer;
  box-shadow: 0 0 4px 0 rgba(0,0,0,0.3);
  transition: all 0.2s ease;
  position: relative;
  z-index: 10; /* Garantindo que o círculo fique visível */
}

input[type="range"]::-webkit-slider-thumb:hover {
  transform: scale(1.1);
  box-shadow: 0 0 6px 0 rgba(0,0,0,0.4);
}

input[type="range"]::-moz-range-thumb {
  height: 22px;
  width: 22px;
  border-radius: 50%;
  background: #F9C349; /* Changed from white to #F9C349 */
  border: 2px solid #F9C349;
  cursor: pointer;
  box-shadow: 0 0 4px 0 rgba(0,0,0,0.3);
  transition: all 0.2s ease;
  position: relative;
  z-index: 10; /* Garantindo que o círculo fique visível */
}

input[type="range"]::-moz-range-thumb:hover {
  transform: scale(1.1);
  box-shadow: 0 0 6px 0 rgba(0,0,0,0.4);
}

input[type="range"]::-ms-thumb {
  height: 22px;
  width: 22px;
  border-radius: 50%;
  background: #F9C349; /* Changed from white to #F9C349 */
  border: 2px solid #F9C349;
  cursor: pointer;
  box-shadow: 0 0 4px 0 rgba(0,0,0,0.3);
  transition: all 0.2s ease;
  position: relative;
  z-index: 10; /* Garantindo que o círculo fique visível */
}

input[type="range"]::-ms-thumb:hover {
  transform: scale(1.1);
  box-shadow: 0 0 6px 0 rgba(0,0,0,0.4);
}

input[type="range"]::-webkit-slider-runnable-track {
  -webkit-appearance: none;
  box-shadow: none;
  border: none;
  background: #E0E0E0; /* Back to the original light gray */
}

input[type="range"]::-moz-range-track {
  box-shadow: none;
  border: none;
  background: #E0E0E0; /* Back to the original light gray */
}

input[type="range"]::-ms-track {
  box-shadow: none;
  border: none;
  background: #E0E0E0; /* Back to the original light gray */
}

/* Style for the second slider (maximum value) */
input[type="range"]:nth-child(2) {
  background: none;
}

/* Custom style for the automatic spacing */
.mt-auto {
  margin-top: 10%;
}

/* Style to keep the description height fixed */
.description-fixed-height {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  -webkit-box-orient: vertical;
  height: 4.5em; /* Fixed height for 3 lines */
  min-height: 4.5em;
  line-height: 1.5em;
  overflow: hidden;
  text-overflow: ellipsis;
}

@media (max-width: 768px) {
  .description-fixed-height {
    height: 2.4em; /* Smaller height for mobile */
    min-height: 2.4em;
    -webkit-line-clamp: 2; /* Only 2 lines on mobile */
    line-clamp: 2;
    line-height: 1.2em;
  }
}

/* Removing the dark gray line styles */

/* Styles for the category filter container */
.category-filter-container {
  background: linear-gradient(145deg, #ffffff, #f8f8f8);
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
  padding: 0.5rem;
  padding-top: 0px;
  transition: all 0.3s ease;
  border: 1px solid rgba(251, 189, 30, 0.1);
  position: relative;
  overflow-y: auto;
  max-height: 70vh; /* Maximum height so the scrollbar shows up when needed */
  scrollbar-width: thin; /* For Firefox */
  scrollbar-color: #FBBD1E #f1f1f1; /* For Firefox */
  scroll-behavior: smooth; /* Rolagem suave */
}

/* Scrollbar styling for WebKit (Chrome, Safari, etc.) */
.category-filter-container::-webkit-scrollbar {
  width: 6px;
}

.category-filter-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}

.category-filter-container::-webkit-scrollbar-thumb {
  background: #FBBD1E;
  border-radius: 10px;
  border: 1px solid rgba(0, 0, 0, 0.1);
}

.category-filter-container::-webkit-scrollbar-thumb:hover {
  background: #e0a800;
}

.category-filter-container::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 4px;
  height: 100%;
  background: linear-gradient(to bottom, #FBBD1E, rgba(251, 189, 30, 0.3));
  border-radius: 4px 0 0 4px;
}

.category-filter-container-mobile {
  background: linear-gradient(145deg, #ffffff, #f8f8f8);
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
  padding: 0.5rem;
  transition: all 0.3s ease;
  border: 1px solid rgba(251, 189, 30, 0.1);
  position: relative;
  overflow-y: auto;
  max-height: 50vh; /* Smaller maximum height for mobile */
  margin-bottom: 1rem;
  scrollbar-width: thin; /* For Firefox */
  scrollbar-color: #FBBD1E #f1f1f1; /* For Firefox */
  scroll-behavior: smooth; /* Rolagem suave */
}

/* Scrollbar styling for WebKit (Chrome, Safari, etc.) */
.category-filter-container-mobile::-webkit-scrollbar {
  width: 6px;
}

.category-filter-container-mobile::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}

.category-filter-container-mobile::-webkit-scrollbar-thumb {
  background: #FBBD1E;
  border-radius: 10px;
  border: 1px solid rgba(0, 0, 0, 0.1);
}

.category-filter-container-mobile::-webkit-scrollbar-thumb:hover {
  background: #e0a800;
}

.category-filter-container-mobile::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 4px;
  height: 100%;
  background: linear-gradient(to bottom, #FBBD1E, rgba(251, 189, 30, 0.3));
  border-radius: 4px 0 0 4px;
}

/* Custom style for the product name with extra thin weight */
.product-name {
  font-weight: 100 !important; /* Forces the thinnest possible weight */
  opacity: 0.7 !important; /* Lowers the opacity further so it looks thinner */
  letter-spacing: 0.03em !important; /* Increases the letter spacing further */
  color: rgba(0, 0, 0, 0.6) !important; /* Lighter color so it looks thinner */
  transform: scale(0.98, 1) !important; /* Comprime ligeiramente na horizontal */
}

/* Overrides to stack the ProductQuantitySelector vertically in List mode */
:deep(.list-actions > div.flex) {
  flex-direction: column !important;
  gap: 8px !important;
  width: 100% !important;
}
:deep(.list-actions > div.flex > button) {
  width: 100% !important;
}
:deep(.list-actions > div.flex > div) {
  width: 100% !important;
}
@media (min-width: 768px) {
  :deep(.list-actions > div.flex > div) {
    width: 100% !important;
  }
}



/* Mobile Grid - make quantity and button the same width */
@media (max-width: 767px) {
  :deep(.grid-mobile-actions > div.flex) { width: 100% !important; }
  :deep(.grid-mobile-actions > div.flex > div) { width: 50% !important; }
  :deep(.grid-mobile-actions > div.flex > button) { width: 50% !important; }
}

</style>
















